import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useDeleteFlow, showDialog } from '../../components/confirm';
import { FormTitle } from '../../components/FormTitle';
import { AppToolbar } from '../../components/AppToolbar/AppToolbar';
import { ExportMenu } from '../../components/AppToolbar/ExportMenu';
import { buildSourceOptions, reconcileSources } from './dupSpikeSource';
import { InlineCellEditor } from '../../components/gridEdit/InlineCellEditor';
import { fmtLocalDate, fmtLocalShort, toWire } from '../../utils/datetime';
// Shared column model + header menu (client 2026-08-05): the NON-frozen data
// columns of the prep-sequence grid get column RESIZE + right-click Hide / Show
// All / Column Chooser (no reorder/sort — the run order is drag-defined; the 7
// frozen columns stay pinned).
import { useColumnManager, useHeaderMenu } from '../../components/AppDataGrid';
import {
  prepBatchApi,
  type QcJob, type QcInstrument, type QcTypeOption,
  type QcLinkedReagent, type QcAvailableReagent,
  type PrepSample, type PrepSeqRow, type PrepHeader, type PrepPreviousBatch,
  type PrepSaveResult,
} from './prepBatch.api';
// COA preview is reagent-scoped (not batch-scoped) — the prep contract has no COA
// endpoint of its own, so the Others-tab COA link reuses the QC reagent COA reader
// (same underlying dbo.Reagent). Read-only reuse; the QC module is not modified.
import { qcBatchApi } from './qcBatch.api';
import { WorkflowStepperBar, type StepState } from './WorkflowStepperBar';
import './qcBatch.css';

// SamplePrep (Yes/No) — decided by a FIXED formula on the entered NTU (turbidity):
// NTU > 1.0 → Yes, otherwise No; blank until a number is typed. Hard-coded on
// purpose (user 2026-08-14) — no longer reads the ACT config. The ACT-driven
// version is preserved in features/settings/act/actData.ts (evalSamplePrep) for
// when ACT persistence lands.
function samplePrepFromNtu(ntu: string | number | null | undefined): '' | 'Yes' | 'No' {
  const n = typeof ntu === 'number' ? ntu : parseFloat(String(ntu ?? '').trim());
  if (!Number.isFinite(n)) return '';
  return n > 1.0 ? 'Yes' : 'No';
}

// ════════════════════════════════════════════════════════════════════════════
// Sample Prep Batch detail screen — the TWIN of QcBatchDetailPage, opened from a
// Sample Prep Queue row (SCREEN 2 of Sample-Prep-Batch-Prototype.html).  The
// test method is ALWAYS known (seeded from the queue row) so there is NO
// Matrix→Test→Method pick cascade, and NO Template / Prep-Batch pickers.  Flow:
// pick Job(s) + Instrument → Load (pending samples) → Sort (server QC
// interleave) → OK (persist).  All shared components (useNotify / useSaveFlow /
// showDialog / AppToolbar / FormTitle / WorkflowStepperBar / fmtLocal*) are
// reused verbatim — nothing bespoke is introduced.
// ════════════════════════════════════════════════════════════════════════════

/** Seed passed in from the Sample Prep Queue row.  `prepOid` set = open an
 *  EXISTING batch (read-only view).  `sort` / `matrixId` / `methodId` are
 *  optional queue-row fields consumed by the save body; they default safely
 *  when the queue does not carry them. */
export interface PrepBatchSeed {
  testMethodId: string;               // primary test (drives QC config); = testMethodIds[0]
  // Multi-test Tier-1 batching: all selected tests from the queue (OG "only tier 1
  // allows multiple tests"). Omitted / single element = a single-test batch.
  testMethodIds?: string[];
  tier:         number;
  matrix?:      string;
  test?:        string;
  method?:      string;
  prepOid?:     string | null;
  // Optional queue-row extras (see the save body). Not required by the flow.
  sort?:        number;
  matrixId?:    string | null;
  methodId?:    string | null;
  // Open a NEW batch READ-ONLY (OG: no SamplePreparation Write → View mode). The
  // queue's New sets this when the user lacks write permission.
  viewOnly?:    boolean;
  // Sample-level pre-selection from the Prep Queue "View details" popup (analysis-
  // queue parity): the picked samples' Job Oids pre-fill jobOids, and their
  // SampleLogIn Oids narrow whatever Load returns down to exactly those samples.
  preSelectedJobOids?:         string[];
  preSelectedSampleLogInOids?: string[];
}

interface Props {
  seed:   PrepBatchSeed;
  onBack: () => void;
  // "Export To QC Batch" (OG btnExportToQCBatch_Execute): after the local
  // validations pass, hand the saved prep batch to the parent, which swaps this
  // detail for the QC Batch form seeded to auto-import the batch's samples.
  onExportToQc?: (ex: {
    prepOid: string; prepBatchId: string;
    testMethodId: string; test: string | null; method: string | null; matrix: string | null;
  }) => void;
}

// Prep-flow island steps (WorkflowStepperBar) — the FULL 9-step workflow from
// Sample-Prep-Batch-Prototype.html STEP_LABELS (line 861), same as the Sample Prep
// Queue island so the pill is continuous queue→detail. "Select test" + "New" are
// the queue pick-head (shown DONE in the detail); "Others" is optional.
const PREP_STEPS = ['Select test', 'New', 'Job ID', 'Instrument', 'Load',
  'Mark samples', 'Sort', 'Others', 'Create Prep Batch'] as const;

// Every Prep Batch acknowledgement uses the shared centered Tier-1 dialog (same
// convention as the QC twin), never a bespoke toast.
// Routine acknowledgement shown as a centered modal dialog (user preference
// 2026-07-16) via the shared showDialog. (error/warning already auto-route to the
// modal via useNotify.)
const prepMsg = (message: string, title = 'Sample Prep Batch', type: 'success' | 'info' = 'success') =>
  void showDialog({ type, title, message });

// Pull the server's OWN envelope error message off an axios error so the user
// sees the real reason, not axios's generic "Request failed with status …".
function apiErrMsg(e: unknown, fallback: string): string {
  const er = e as { response?: { data?: { error?: { message?: string } } }; isAxiosError?: boolean; message?: string };
  // Twin of the QC form's helper — see the comment there. A CONTROLLED rejection
  // (HTTP 200 + {success:false,error}) reaches us as a plain Error from `unwrap`,
  // which has no `.response`, so without this branch the API's real message was
  // swallowed and replaced by the generic fallback. (fix 2026-08-07)
  const envelope = er.response?.data?.error?.message;
  if (envelope) return envelope;
  if (!er.response && !er.isAxiosError && typeof er.message === 'string' && er.message) return er.message;
  return fallback;
}

// ── local grid row model ─────────────────────────────────────────────────────
type RowKind = 'sample' | 'qc' | 'dilution';
interface PrepRow {
  id: string; kind: RowKind; qc: string; sort: number | null; checked: boolean;
  seqOid?: string | null;
  code: string; job: string; component: string; sid: string;
  df: number; finalVol: string;
  dupSource: string; spikeSource: string; sourceId?: string;
  originalSysSampleCode: string | null; isReport: boolean;
  units: string; sampleName: string; siteName: string; samplingAddress: string;
  dateCreated: string | null; dateReceived: string | null; dateCollected: string | null;
  client: string; projectId: string; projectName: string; holdTime: string;
  // prep-specific display columns
  prepMethodOid?: string | null; prepMethod: string;
  // NTU (turbidity) the analyst enters; drives the computed SamplePrep column via
  // the ACT rule for this test/method. Kept as a free-typed string like the other
  // numeric grid cells. (feature 2026-08-14)
  ntu: string;
  prepOptionsOid?: string | null; prepOptions: string;
  functionTypeOid?: string | null; functionType: string;
  datePrepared: string | null; takenSampleUnit?: string | null; multiplier?: number | null;
  // carried for persistence
  qcTypeOid?: string | null; sampleLogInOid?: string | null;
  systemId?: string; strSampleId?: string; runno?: number; dilutionCount: number;
  sampleAmount: string; excludedReport: boolean;
}
let uidN = 0;
const uid = () => 'pr' + (++uidN);

// DatePrepared default for freshly loaded samples: the current local time as the
// naive wire form (shared toWire; ADR-0008 kill-switch → literal local, no shift)
// so the grid shows the real "now". The cell itself edits via the shared
// <InlineCellEditor type="datetime"> (global datetime picker + fmtLocalDateTime).
const datePreparedNow = () => toWire(new Date());

/** DilutionCount editable cell (user 2026-07-15): keeps a local string draft so the
 *  field can be CLEARED and retyped freely (the value isn't force-reset on each
 *  keystroke).  On blur / end-edit, an empty or invalid value defaults back to 1
 *  (the DilutionCount minimum); a valid integer ≥ 1 is committed. */
function DilutionCell({ value, onCommit }: { value: number; onCommit: (n: number) => void }) {
  const [draft, setDraft] = useState(String(value ?? 1));
  useEffect(() => { setDraft(String(value ?? 1)); }, [value]);
  const commit = () => {
    const n = Math.floor(Number(draft));
    const val = Number.isFinite(n) && n >= 1 ? n : 1;   // empty/invalid/<1 → default 1
    setDraft(String(val));
    onCommit(val);
  };
  return (
    <input className="cellinp num" type="number" min={1}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onClick={e => e.stopPropagation()}
      onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      onBlur={commit} />
  );
}

const seqRowToLocal = (r: PrepSeqRow): PrepRow => ({
  id: uid(), kind: r.kind, qc: r.qcType, sort: r.sort, checked: false,
  seqOid: r.seqOid ?? null,
  code: r.code, job: r.jobId ?? '', component: r.component ?? '',
  // SampleID column binds the SAMPLE LINK only (legacy SamplePrepBatchSequence.SampleID);
  // a standalone QC row (Method Blank, CCB, ICV…) stays BLANK — the old
  // `?? r.strSampleId` fallback showed that row's own run code there. Twin of the
  // QC-form fix. (fix 2026-08-07)
  sid: r.sampleId ?? '',
  df: r.df, finalVol: r.finalVol ?? '',
  dupSource: r.dupSource ?? '', spikeSource: r.spikeSource ?? '', sourceId: r.sourceId ?? undefined,
  originalSysSampleCode: r.originalSysSampleCode ?? null, isReport: r.isReport ?? false,
  units: r.units ?? '', sampleName: r.sampleName ?? '', siteName: r.siteName ?? '',
  samplingAddress: r.samplingAddress ?? '',
  dateCreated: r.dateCreated, dateReceived: r.dateReceived, dateCollected: r.dateCollected,
  client: r.client ?? '', projectId: r.projectId ?? '', projectName: r.projectName ?? '',
  holdTime: r.holdTime ?? '',
  prepMethodOid: r.prepMethodOid, prepMethod: r.prepMethod ?? '',
  ntu: r.ntu != null && r.ntu !== '' ? String(r.ntu) : '',
  prepOptionsOid: r.prepOptionsOid, prepOptions: r.prepOptions ?? '',
  functionTypeOid: r.functionTypeOid, functionType: r.functionType ?? '',
  datePrepared: r.datePrepared, takenSampleUnit: r.takenSampleUnit, multiplier: r.multiplier,
  qcTypeOid: r.qcTypeOid, sampleLogInOid: r.sampleLogInOid,
  // No `?? r.code` fallback — OG persists SystemID NULL on Sample rows and the
  // SHORT run code on QC rows; falling back to the full code would corrupt both.
  systemId: r.systemId ?? '', strSampleId: r.strSampleId ?? undefined, runno: r.runno,
  dilutionCount: r.dilutionCount ?? 1, sampleAmount: r.sampleAmount ?? '', excludedReport: r.excludedReport ?? false,
});

const sampleToLocal = (s: PrepSample): PrepRow => ({
  id: uid(), kind: 'sample', qc: 'Sample', sort: 0, checked: false,
  code: s.sysSampleCode ?? '', job: s.jobID ?? '', component: s.component ?? '',
  sid: s.sysSampleCode ?? '',
  df: 1, finalVol: s.finalVolMl ?? '',
  dupSource: '', spikeSource: '',
  originalSysSampleCode: null, isReport: false,
  units: s.units ?? '', sampleName: s.sampleName ?? '', siteName: s.siteName ?? '',
  samplingAddress: s.samplingAddress ?? '',
  dateCreated: s.dateCreated, dateReceived: s.dateReceived, dateCollected: s.dateCollected,
  client: s.client ?? '', projectId: s.projectID ?? '', projectName: s.projectName ?? '',
  holdTime: s.holdTime ?? '',
  prepMethodOid: s.prepMethodOid, prepMethod: s.prepMethod ?? '',
  ntu: '',
  prepOptionsOid: null, prepOptions: '',
  // FunctionType = SamplePrepType (read-only). DatePrepared defaults to "now"
  // (naive local wire form) and is editable pre-Sort.
  functionTypeOid: null, functionType: s.samplePrepType ?? '',
  datePrepared: datePreparedNow(), takenSampleUnit: null, multiplier: null,
  qcTypeOid: null, sampleLogInOid: s.sampleLogInOid,
  // SystemID stays empty on Sample rows (OG persists NULL — live-data verified).
  systemId: '', strSampleId: s.sysSampleCode ?? '', runno: 1,
  dilutionCount: s.dilutionCount > 0 ? s.dilutionCount : 1, sampleAmount: '', excludedReport: s.excludedReport,
});

// Local grid row → wire shape, shared by Sort/Add QC/Remove QC/Save (all POST
// the CURRENT full grid to the server).
const toWireRow = (r: PrepRow): PrepSeqRow => ({
  seqOid: r.seqOid ?? null,
  kind: r.kind, qcType: r.qc, qcTypeOid: r.qcTypeOid ?? null,
  sort: r.sort ?? 0, runno: r.runno ?? 1, code: r.code, systemId: r.systemId || null,
  jobId: r.job || null, component: r.component || null,
  strSampleId: r.strSampleId ?? null, sampleId: r.sid || null,
  sampleLogInOid: r.sampleLogInOid ?? null, df: r.df,
  sampleAmount: r.sampleAmount || null, finalVol: r.finalVol || null,
  dupSource: r.dupSource || null, spikeSource: r.spikeSource || null,
  sourceId: r.sourceId ?? null,
  originalSysSampleCode: r.originalSysSampleCode ?? null, isReport: r.isReport ?? false,
  dilutionCount: r.dilutionCount, excludedReport: r.excludedReport,
  units: r.units || null, sampleName: r.sampleName || null,
  siteName: r.siteName || null, samplingAddress: r.samplingAddress || null,
  dateCreated: r.dateCreated, dateReceived: r.dateReceived, dateCollected: r.dateCollected,
  client: r.client || null, projectId: r.projectId || null,
  projectName: r.projectName || null, holdTime: r.holdTime || null,
  prepMethodOid: r.prepMethodOid ?? null, prepMethod: r.prepMethod || null,
  ntu: r.ntu?.trim() && Number.isFinite(Number(r.ntu)) ? Number(r.ntu.trim()) : null,
  prepOptionsOid: r.prepOptionsOid ?? null, prepOptions: r.prepOptions || null,
  functionTypeOid: r.functionTypeOid ?? null, functionType: r.functionType || null,
  datePrepared: r.datePrepared, takenSampleUnit: r.takenSampleUnit ?? null, multiplier: r.multiplier ?? null,
});

// Signature of the edit-mode savable state (sequence + Temp/Humidity/Comment/
// Instrument — exactly what saveEdits→updateBatch persists). Drives the OK
// dirty-gate: OK is enabled only once this changes vs the loaded baseline.
// Twin of QcBatchDetailPage.editSig.
const editSig = (rows: PrepRow[], temperature: string, humidity: string, comment: string, instrument: string | null): string =>
  JSON.stringify({ s: rows.map(toWireRow), t: temperature, h: humidity, c: comment, i: instrument });

export default function PrepBatchDetailPage({ seed, onBack, onExportToQc }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();

  // Test/Method/Matrix/Tier — from the seed for a new batch; overwritten from
  // the header once an existing batch's detail loads.
  // Stateful so an EXISTING batch (opened from the list — seed.testMethodId is '')
  // resolves its real test method from the loaded header; otherwise the reference
  // load (jobs / instruments / QC types / prep options) is skipped and the QCType
  // panel stays empty.
  const [effTestMethodId, setEffTestMethodId] = useState(seed.testMethodId);
  // All selected test methods (multi-test Tier-1 batch). Jobs / Instruments / Load /
  // Save span every test; the QC-interleave config (Sort/Add-QC) keeps the primary.
  // Stateful + empties filtered: an EXISTING batch seeds testMethodId '' → this would
  // otherwise be [''], and Add Sample (POST /load) sends it as Guid[] → '' fails to
  // bind → 500 "Failed to load candidate samples". loadDetail resolves the real list.
  const [effTestMethodIds, setEffTestMethodIds] = useState<string[]>(
    seed.testMethodIds && seed.testMethodIds.length > 0
      ? seed.testMethodIds
      : (seed.testMethodId ? [seed.testMethodId] : []));
  const [effTest, setEffTest]     = useState<string | null>(seed.test ?? null);
  const [effMethod, setEffMethod] = useState<string | null>(seed.method ?? null);
  const [effMatrix, setEffMatrix] = useState<string | null>(seed.matrix ?? null);
  const [tier, setTier]           = useState<number>(seed.tier);
  // Prep sort (queue "PB-<tier>-<sort>") — the queue row can pass it; default 0.
  const sort = seed.sort ?? 0;

  // Read-only when opening an existing batch, OR a NEW batch the user lacks write
  // permission for (seed.viewOnly — OG SamplePreparation no-Write → View mode).
  const [viewOnly, setViewOnly] = useState<boolean>(!!seed.prepOid || !!seed.viewOnly);
  const [existingHeader, setExistingHeader] = useState<PrepHeader | null>(null);

  // ── reference data ──
  const [jobsList, setJobsList]   = useState<QcJob[]>([]);
  const [instrList, setInstrList] = useState<QcInstrument[]>([]);
  const [qcTypeList, setQcTypeList] = useState<QcTypeOption[]>([]);
  const [loadedSamples, setLoadedSamples] = useState<PrepSample[]>([]);

  // ── selections / form state ──
  const [jobOids, setJobOids]       = useState<string[]>(seed.preSelectedJobOids ?? []);
  // Sample-level narrowing from the Prep Queue popup (null = no narrowing → Load
  // returns the job's full pending-prep list, as before).
  const [preSelectedSlOids] = useState<string[] | null>(seed.preSelectedSampleLogInOids ?? null);
  const [instrument, setInstrument] = useState<string | null>(null);
  const [loaded, setLoaded]         = useState(false);
  const [sorted, setSorted]         = useState(false);
  const [rows, setRows]             = useState<PrepRow[]>([]);
  const [tab, setTab]               = useState<'seq' | 'others'>('seq');
  const [openPop, setOpenPop]       = useState<'job' | 'instr' | null>(null);
  const [giCollapsed, setGiCollapsed] = useState(false);
  const [qcCollapsed, setQcCollapsed] = useState(false);
  const [fullGrid, setFullGrid]     = useState(false);
  const [temperature, setTemperature] = useState('');
  const [humidity, setHumidity]     = useState('');
  const [comment, setComment]       = useState('');
  // Edit-mode OK dirty-gate (QC-form parity): OK is enabled only once the loaded
  // batch actually changed. `editBaseline` = the just-loaded savable signature
  // (captured in loadDetail); `tabsDirty` flags an Others-tab (reagent/instrument)
  // edit. Both reset on (re)load.
  const editBaseline = useRef<string | null>(null);
  const [tabsDirty, setTabsDirty] = useState(false);
  const [savedPrepId, setSavedPrepId] = useState<string | null>(null);
  const [modal, setModal] = useState<null | 'notes' | 'instr2' | 'reagent' | 'addsample' | 'copy'>(null);

  // ── Prep-sequence grid column model (client 2026-08-05: RESIZE + Hide / Show All /
  // Column Chooser on the NON-frozen data columns; the 7 frozen leading columns stay
  // pinned; NO reorder/sort — the run order is drag-defined). visibleColumns drives
  // BOTH header and body so a hidden column drops from both; persistKey restores it.
  const PREP_DATA_COLS = useMemo(() => [
    { key: 'dilution',        label: sorted ? 'DF' : 'DilutionCount' },
    { key: 'sampleAmount',    label: 'SampleAmount' },
    { key: 'finalVol',        label: 'FinalVol(ml)' },
    { key: 'dupSource',       label: 'DupSource' },
    { key: 'spikeSource',     label: 'SpikeSource' },
    { key: 'sampleName',      label: 'SampleName' },
    { key: 'siteName',        label: 'SiteName' },
    { key: 'samplingAddress', label: 'SamplingAddress' },
    { key: 'units',           label: 'Units' },
    { key: 'prepMethod',      label: 'PrepMethod' },
    { key: 'ntu',             label: 'NTU' },
    { key: 'samplePrep',      label: 'SamplePrep' },
    { key: 'functionType',    label: 'FunctionType' },
    { key: 'holdTime',        label: 'HoldTime' },
    { key: 'datePrepared',    label: 'DatePrepared' },
    { key: 'dateReceived',    label: 'DateReceived' },
    { key: 'dateCollected',   label: 'DateCollected' },
    { key: 'client',          label: 'Client' },
    { key: 'projectId',       label: 'ProjectID' },
  ], [sorted]);
  // Storage key bumped to -v2 when the column set changed (PrepOptions → NTU +
  // SamplePrep, 2026-08-14) so a stale saved layout can't hide the new columns.
  const seqCm = useColumnManager(PREP_DATA_COLS, c => c.key, c => c.label, 'prepbatch-detail-v2');
  const seqMenu = useHeaderMenu({ sort: { key: null, dir: null }, setSortExplicit: () => {} }, seqCm.columnMenu);
  const seqW = (k: string) => {
    const w = seqCm.colWidth(k);
    return w ? { width: w, minWidth: w, maxWidth: w } : {};
  };
  // ── DupSource / SpikeSource selection combos ────────────────────────────────
  // Legacy prep has these too (SamplePreparationController :2368/:3455) with a rule
  // byte-identical to the QC form's, so both call the ONE shared builder in
  // ./dupSpikeSource. Recomputed from the LIVE grid so add/remove/renumber re-narrows
  // every dropdown; a row absent from the map has no valid sources → plain text.
  const sourceOpts = useMemo(
    () => buildSourceOptions(rows, qcTypeList),
    [rows, qcTypeList]);

  // Keep stored Dup/Spike sources valid as rows come and go — twin of the QC form's
  // reconcile (user 2026-08-07): a now-invalid value adopts the only remaining
  // candidate, or clears when there are several. Only writes on a real change.
  useEffect(() => {
    if (!sorted) return;
    const fixes = reconcileSources(rows, sourceOpts);
    if (fixes.length === 0) return;
    const byId = new Map(fixes.map(f => [f.id, f]));
    setRows(prev => prev.map(r => {
      const f = byId.get(r.id);
      return f ? { ...r, ...f, id: r.id } : r;
    }));
  }, [rows, sorted, sourceOpts]);

  // ── "Export to" (legacy XAF default ExportController above the sequence grid) ──
  // Twin of the QC Batch form's export — the SAME shared <ExportMenu> and the same
  // 10 ALPACA formats (rule 8).  Prep's frozen data columns are QCType · Sort ·
  // SysSamplecode · JobID (no Component/SampleID here), then the CURRENTLY VISIBLE
  // data columns, so Hide / Column Chooser and the DilutionCount ⇄ DF caption swap
  // carry into the file.  Cell text mirrors the <td> expressions (dates via the
  // shared fmtLocal* helper §14b.6; JobID blank on non-Sample rows, OG parity).
  const seqExportText = (r: PrepRow, key: string): string => {
    switch (key) {
      case 'dilution':      return String((sorted ? r.df : r.dilutionCount) || '');
      case 'dupSource':     return sorted ? (r.dupSource || '') : '';
      case 'spikeSource':   return sorted ? (r.spikeSource || '') : '';
      case 'datePrepared':  return fmtLocalShort(r.datePrepared);
      case 'dateReceived':  return fmtLocalShort(r.dateReceived);
      case 'dateCollected': return fmtLocalShort(r.dateCollected);
      case 'samplePrep':    return samplePrepFromNtu(r.ntu);  // computed, not a row field
      default:              return String((r as unknown as Record<string, unknown>)[key] ?? '');
    }
  };
  const seqExport = useMemo(() => ({
    headers: ['QCType', 'Sort', 'SysSamplecode', 'JobID',
              ...seqCm.visibleColumns.map(c => c.label)],
    rows: rows.map(r => [
      r.qc, r.sort ?? '', r.code || '', r.qc === 'Sample' ? (r.job || '') : '',
      ...seqCm.visibleColumns.map(c => seqExportText(r, c.key)),
    ].map(String)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seqExportText is a pure
    // formatter recreated each render; the data it reads is covered by rows/sorted.
  }), [rows, sorted, seqCm.visibleColumns]);
  const seqHClass: Record<string, string> = {
    dilution:     `num ${sorted ? 'col-df' : 'col-num-sm'}`,
    sampleAmount: 'num col-num-sm',
    finalVol:     'num',
    dupSource:    'col-dupspike',
    spikeSource:  'col-dupspike',
  };
  // Add Sample — pending-prep candidates fetched on demand, shown in the picker.
  const [addSampleCandidates, setAddSampleCandidates] = useState<PrepSample[]>([]);

  // ── Others tab: Reagents (legacy ReagentLink_Execute parity — mirrors the QC
  // twin).  POST-save rows are server-loaded once savedPrepId is set; PRE-save
  // picks are staged locally and shipped atomically with Create. ─────────────
  const [reagentRows, setReagentRows] = useState<QcLinkedReagent[]>([]);
  const [reagentSel, setReagentSel]   = useState<Set<string>>(new Set());
  const [stagedReagents, setStagedReagents] = useState<QcAvailableReagent[]>([]);
  const [stagedReagentSel, setStagedReagentSel] = useState<Set<string>>(new Set());
  // Others tab: Instruments (legacy InstrumentLink_Execute parity) — same
  // saved-vs-staged split as reagents.
  const [instrumentRows, setInstrumentRows] = useState<QcInstrument[]>([]);
  const [instrumentSel, setInstrumentSel]   = useState<Set<string>>(new Set());
  const [stagedInstruments, setStagedInstruments] = useState<QcInstrument[]>([]);
  const [stagedInstrumentSel, setStagedInstrumentSel] = useState<Set<string>>(new Set());

  // ── load reference data for the (known) test method ──
  useEffect(() => {
    if (!effTestMethodId) return;
    let cancelled = false;
    (async () => {
      try {
        const [j, i, q] = await Promise.all([
          prepBatchApi.jobs(effTestMethodId, sort, tier, effTestMethodIds),
          prepBatchApi.instruments(effTestMethodId, effTestMethodIds),
          prepBatchApi.qcTypes(effTestMethodId),
        ]);
        if (cancelled) return;
        setJobsList(j); setInstrList(i); setQcTypeList(q);
      } catch {
        if (!cancelled) notify.error('Failed to load prep batch reference data — check the API logs.');
      }
    })();
    return () => { cancelled = true; };
  }, [effTestMethodId, sort, tier, notify]);

  // Project Notes count for the toolbar caption — job-scoped, same shared feed
  // the NotesModal reads.
  const [notesCount, setNotesCount] = useState<number | null>(null);
  const loadNotesCount = useCallback(async () => {
    const job = jobOids[0];
    if (!job) { setNotesCount(null); return; }
    try {
      const r = await api.get(`/api/v1/reporting/custom/${encodeURIComponent(job)}/project-notes`,
        { silentLoading: true } as never);
      setNotesCount(((r.data as { data?: unknown[] }).data ?? []).length);
    } catch { setNotesCount(null); }
  }, [jobOids]);
  useEffect(() => { void loadNotesCount(); }, [loadNotesCount]);

  // Open an existing prep batch (view mode) — header + built sequence.
  const loadDetail = useCallback(async (prepOid: string) => {
    const { header, sequence } = await prepBatchApi.getDetail(prepOid);
    setExistingHeader(header);
    // Resolve the real test method (header.testMethodId may be a ';'-joined list for
    // a multi-test batch — the QCType panel keys off the PRIMARY test) so the
    // reference load runs and the QCType panel populates.
    const testList = header.testMethodId?.split(';').map(s => s.trim()).filter(Boolean) ?? [];
    if (testList.length > 0) { setEffTestMethodId(testList[0]!); setEffTestMethodIds(testList); }
    setEffTest(header.test); setEffMethod(header.method); setEffMatrix(header.matrix);
    if (header.tier != null) setTier(header.tier);
    setTemperature(header.temperature ?? '');
    setHumidity(header.humidity ?? '');
    setComment(header.comments ?? '');
    setInstrument(header.instrumentOids);
    setJobOids(header.jobOids ? header.jobOids.split(';').filter(Boolean) : []);
    const localRows = sequence.map(seqRowToLocal);
    setRows(localRows);
    setLoaded(true); setSorted(true);
    setSavedPrepId(header.prepBatchOid);
    // Baseline for the OK dirty-gate: the just-loaded savable state (nothing to save
    // until this signature changes). tabsDirty resets too.
    editBaseline.current = editSig(localRows, header.temperature ?? '', header.humidity ?? '', header.comments ?? '', header.instrumentOids);
    setTabsDirty(false);
    // A saved batch opens EDITABLE (A1 — mirror the QC form's open-existing→edit),
    // UNLESS the caller asked for VIEW mode (row-click, seed.viewOnly) or the user
    // has no write permission. A ResultCompleted batch (status 3) always stays
    // read-only (same gate the update SP enforces + the Delete gate). This preserves
    // the QC-form distinction: row-click → view, Edit → edit.
    setViewOnly(seed.viewOnly === true || header.status === 3);
  }, []);

  useEffect(() => {
    if (!seed.prepOid) return;
    let cancelled = false;
    (async () => {
      try { if (!cancelled) await loadDetail(seed.prepOid!); }
      catch { if (!cancelled) notify.error('Failed to load the prep batch — check the API logs.'); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed.prepOid]);

  // Prep Queue "View details" → OK: the analyst ticked their samples in the popup,
  // so land them in the grid immediately (analysis-queue → QC-form parity — the
  // picked samples "load" on OK, no manual Load click). New batch only; runs once.
  // The instrument is still required before Sort/OK, so this uses force=true to
  // bypass only the Load-button's instrument gate (the load API doesn't need it).
  const autoLoadedFromQueue = useRef(false);
  useEffect(() => {
    if (autoLoadedFromQueue.current) return;
    if (seed.prepOid) return;                                   // existing batch → loadDetail
    if (!preSelectedSlOids || preSelectedSlOids.length === 0) return;
    if (!effTestMethodId || jobOids.length === 0) return;
    autoLoadedFromQueue.current = true;
    void loadSamples({ force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effTestMethodId, jobOids, preSelectedSlOids, seed.prepOid]);

  // ── stepper status ──
  const stepStatus = useMemo<StepState[]>(() => {
    // Prototype stepStatus() (Sample-Prep-Batch-Prototype.html :866) for the DETAIL
    // screen: Select test(0)+New(1) done; Job ID(2)→Instrument(3)→Load(4)→Mark(5)→
    // Sort(6) progress; Others(7) optional (done once records/saved); Create Prep
    // Batch(8) active after Sort, done once saved.
    const st: StepState[] = new Array(PREP_STEPS.length).fill('todo');
    st[0] = 'done'; st[1] = 'done';                               // Select test, New
    const jobOk = jobOids.length > 0;
    st[2] = jobOk ? 'done' : 'active';                            // Job ID
    if (jobOk) st[3] = instrument ? 'done' : 'active';            // Instrument
    if (jobOk && instrument) st[4] = loaded ? 'done' : 'active';  // Load
    if (loaded) {
      // Once Sort has run the checkboxes are cleared, so `marked` alone would flip
      // step 6 back to incomplete — treat `sorted` as proof the samples were marked.
      const marked = sorted || rows.some(r => r.checked);
      st[5] = marked ? 'done' : 'active';                         // Mark samples
      if (marked) st[6] = sorted ? 'done' : 'active';             // Sort
    }
    if (sorted) { st[5] = 'done'; st[6] = 'done'; st[7] = 'optional'; st[8] = 'active'; }  // Mark+Sort done, Others optional, Create active
    if (savedPrepId) { st[7] = 'done'; st[8] = 'done'; }
    return st;
  }, [jobOids, instrument, loaded, sorted, rows, savedPrepId]);

  const renumber = (arr: PrepRow[]) => arr.map((r, i) => ({ ...r, sort: i }));

  // Patch one grid row (pre-Sort inline edits: DilutionCount / SampleAmount /
  // FinalVol / PrepOptions / DatePrepared). Edits persist in `rows` and survive
  // Sort (see buildSequence) and Save (toWireRow carries every prep field).
  const updateRow = (id: string, patch: Partial<PrepRow>) =>
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
  // OG SamplePrepLoad_Execute (:4583) gates Load on Job+Matrix+Test+Method only —
  // NOT Instrument (instrument is optional at Load). effTestMethodId already covers
  // matrix/test/method, so Job selection is the effective gate.
  const loadEnabled = jobOids.length > 0;

  // OG locks EVERY header field once the batch is sorted (`enbstat=false` when the
  // Sort action has flipped to "Ok" — OnViewControlsCreated :1638-1641), so Temp /
  // Humidity must be entered BEFORE Sort; "Previous" unlocks them again. Job ID and
  // Instrument already carry `sorted` in their own pickers. Scoped to the CREATE
  // flow: an existing saved batch stays editable, which is OG's History-view save
  // (SavePrepBatch_Execute) and AI's edit-existing feature.
  const headerLocked = viewOnly || (sorted && !existingHeader);
  // Export To QC Batch shown in the detail toolbar (OG enables it on saved batches;
  // re-enabled 2026-07-25). runExportToQc gates it to a saved batch at the right status.
  const showExportToQc = true;

  // OK dirty-gate (QC-form parity): true once the sequence / Temp / Humidity /
  // Comment / Instrument changed vs the loaded baseline, or an Others-tab
  // (reagent / instrument) edit was made. Drives the edit-mode OK button.
  const editDirty = useMemo(
    () => tabsDirty || (editBaseline.current !== null
      && editSig(rows, temperature, humidity, comment, instrument) !== editBaseline.current),
    [tabsDirty, rows, temperature, humidity, comment, instrument]);

  // ── picker labels ──
  const jobLabel = existingHeader?.jobIds
    ? existingHeader.jobIds
    : jobOids.length
      ? jobsList.filter(j => jobOids.includes(j.jobOid)).map(j => j.jobID).join(';')
      : 'Select job(s)…';
  const instrLabel = instrument
    ? (instrList.find(x => x.instrumentOid === instrument)?.instrumentName ?? instrument)
    : 'Select instrument…';

  // ── actions ──
  function toggleJob(oid: string) {
    setJobOids(prev => (prev.includes(oid) ? prev.filter(x => x !== oid) : [...prev, oid]));
    if (loaded) { setLoaded(false); setSorted(false); setRows([]); setLoadedSamples([]); }
  }
  function pickInstr(oid: string) {
    setInstrument(prev => (prev === oid ? null : oid));
    if (instrument !== oid) setOpenPop(null);
  }

  // `force` bypasses the Load-button gate (which also requires an Instrument) so
  // the Prep Queue "View details" → OK auto-load can populate the grid before the
  // analyst picks an instrument. The load API itself doesn't need the instrument
  // (it's a UI gate only); the instrument is still required before Sort/OK/Save.
  async function loadSamples(opts?: { force?: boolean }) {
    if (!opts?.force && !loadEnabled) return;
    try {
      let samples = await prepBatchApi.load({ testMethodId: effTestMethodId, testMethodIds: effTestMethodIds, jobOids, sort, tier });
      // Prep Queue "View details" popup parity (analysis-queue → QC flow): narrow
      // the job's full pending list down to exactly the samples the user ticked.
      if (preSelectedSlOids && preSelectedSlOids.length > 0) {
        const keep = new Set(preSelectedSlOids);
        samples = samples.filter(s => keep.has(s.sampleLogInOid));
      }
      setLoadedSamples(samples);
      setRows(samples.map(sampleToLocal));
      setLoaded(true); setSorted(false);
      if (samples.length === 0) notify.warning('No pending samples found for the selected job(s).');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to load samples.'));
    }
  }

  function resetBatch() {
    setJobOids([]); setInstrument(null);
    setLoaded(false); setSorted(false); setRows([]); setLoadedSamples([]);
    setTab('seq');
  }

  // ── Add Sample (OG SamplePreparationController.AddSample_Execute :6839) ───────
  // OG opens a popup ListView of pending-prep candidates for the batch's
  // test/matrix/method ACROSS ALL JOBS, EXCLUDING SampleLogIns already in the
  // sequence, then Dc_Accepting (:6882) appends each pick as a new "Sample"
  // sequence row stamped with the same PrepMethod/HoldTime/Units as Load. AI
  // matches OG's cross-all-jobs scope: jobsList is exactly the set of jobs with a
  // pending-prep sample for this test (AI_Analysis_PrepBatch_Jobs_SP — same
  // PendingPreparation gate as Load, no batch restriction), so widen the candidate
  // query to every candidate job (fall back to the batch's own jobs if the job
  // list hasn't loaded yet). The stamping is identical — the Load SP resolves
  // PrepMethod/SamplePrepType/Units per candidate, so sampleToLocal yields fully-
  // stamped rows that match the rest of the batch.
  async function openAddSample() {
    const used = rows
      .filter(r => r.kind === 'sample' && r.sampleLogInOid)
      .map(r => r.sampleLogInOid!) as string[];
    const candidateJobOids = jobsList.length > 0 ? jobsList.map(j => j.jobOid) : jobOids;
    try {
      const candidates = await prepBatchApi.load({
        testMethodId: effTestMethodId, testMethodIds: effTestMethodIds, jobOids: candidateJobOids, sort, tier, excludeSampleLogInOids: used,
      });
      if (candidates.length === 0) { notify.warning('No more samples available to add.'); return; }
      setAddSampleCandidates(candidates);
      setModal('addsample');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to load candidate samples.'));
    }
  }

  // Picker OK — append the picked candidates as new Sample rows. Pre-Sort they join
  // the loadedSamples pool (buildSequence sources the Sort from it by SysSampleCode)
  // and the sample list; post-Sort they land at the end of the run sequence and are
  // renumbered so they flow through toWireRow → Save unchanged.
  function commitAddSample(selected: PrepSample[]) {
    setModal(null);
    if (selected.length === 0) return;
    const newRows = selected.map(sampleToLocal);
    setRows(prev => (sorted ? renumber([...prev, ...newRows]) : [...prev, ...newRows]));
    if (!sorted) setLoadedSamples(prev => [...prev, ...selected]);
    prepMsg(`${selected.length} sample(s) added.`, 'Add Sample');
  }

  // Sort → server-side QC interleave from the CHECKED loaded samples.
  async function buildSequence() {
    const sampleRows = rows.filter(r => r.kind === 'sample');
    const checkedCodes = new Set(sampleRows.filter(r => r.checked).map(r => r.code));
    if (checkedCodes.size === 0) { notify.warning('Select at least one sample (tick its checkbox) before Sort.'); return; }
    // Every SELECTED sample must carry a Sort value — OG SamplePrepSort_Execute
    // (:4997-5003), verbatim twin of the QC form's guard (QCBatchController :6953).
    // (fix 2026-08-07)
    const checkedRows = sampleRows.filter(r => r.checked);
    if (checkedRows.some(r => (r.sort ?? 0) <= 0)) {
      notify.warning('Enter the sort value for selected samples');
      return;
    }
    // Build the payload in the analyst's SORT ORDER — OG AddSequence takes the sample
    // list as `…Where(x => x.SampleID != null).OrderBy(a => a.Sort)`
    // (SamplePreparationController.cs:7048). Previously this filtered `loadedSamples`
    // (the SP's load order), silently discarding a deliberate out-of-order selection.
    // Twin of the QC-form fix. (fix 2026-08-07)
    const loadedByCode = new Map(loadedSamples.map(s => [s.sysSampleCode ?? '', s]));
    const toSort = checkedRows
      .slice()
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
      .flatMap(r => { const s = loadedByCode.get(r.code); return s ? [s] : []; });
    if (toSort.length === 0) { notify.warning('Select at least one sample (tick its checkbox) before Sort.'); return; }
    // OG SamplePrepSort_Execute (:5117-5130) requires an Instrument at Sort. If none
    // is selected: warn to pick one when the test HAS linked instruments, else block
    // with the "not linked to this test" message (verbatim OG). Instrument is NOT
    // required to Load — only to Sort.
    if (!instrument) {
      if (instrList.length > 0) notify.warning('Please select an instrument.');
      else notify.error('Instrument has not been linked to this test. Get assistance from your LIMS administrator to add the instruments.');
      return;
    }
    // Snapshot the pre-Sort inline edits per sample — the build endpoint maps
    // samples to QcBatchSampleRow, which DROPS the prep-only fields, so re-apply
    // them onto the built rows afterwards (keyed by sampleLogInOid).
    const editByOid = new Map(sampleRows
      .filter(r => r.sampleLogInOid)
      .map(r => [r.sampleLogInOid!, {
        dilutionCount: r.dilutionCount, sampleAmount: r.sampleAmount, finalVol: r.finalVol,
        prepOptionsOid: r.prepOptionsOid, prepOptions: r.prepOptions,
        functionTypeOid: r.functionTypeOid, functionType: r.functionType, datePrepared: r.datePrepared,
        ntu: r.ntu,   // analyst-entered turbidity survives Sort (feature 2026-08-14)
      }] as const));
    // A prep batch has ONE prep method (the tier's) — the build drops PrepMethod
    // (QcBatchSampleRow has no such field), so stamp EVERY built row with the
    // common method afterwards. OG parity: QC rows (CCB/ICV/…) carry the anchor
    // sample's PrepMethod too, not just the sample rows.
    // OG SamplePreparationController AddQC (:5322-5331) copies the anchor sample's
    // Units / HoldTime / PrepMethod / FunctionType / DatePrepared onto a standalone
    // QC row, so OG shows them on ALL rows. These are batch/test/method-level
    // (identical for every sample of the batch), so sampleRows[0] is a valid common
    // source — same pattern as PrepMethod. The shared QcSequenceBuilder.MakeLeadQc /
    // MakeSampleQc deliberately leave them blank on QC rows (QC-form rule); we
    // re-stamp here in the PREP grid only, never touching the shared builder.
    // (Sample-Prep column parity, 2026-07-15)
    const commonPrepMethod     = sampleRows[0]?.prepMethod ?? '';
    const commonPrepMethodOid  = sampleRows[0]?.prepMethodOid ?? null;
    const commonUnits          = sampleRows[0]?.units ?? '';
    const commonHoldTime       = sampleRows[0]?.holdTime ?? '';
    const commonFunctionType   = sampleRows[0]?.functionType ?? '';
    const commonFunctionTypeOid= sampleRows[0]?.functionTypeOid ?? null;
    const commonDatePrepared   = sampleRows[0]?.datePrepared ?? null;
    // PrepOptions (PrepNameOptions) is a PER-SAMPLE user selection, not a batch
    // constant — OG copies the ANCHOR sample's value onto QC rows (ctrl :5330).
    // So stamp it onto QC rows ONLY (from the anchor sample), and leave each
    // sample row's own selection (including a deliberate blank) untouched.
    const commonPrepOptions    = sampleRows[0]?.prepOptions ?? '';
    const commonPrepOptionsOid = sampleRows[0]?.prepOptionsOid ?? null;
    try {
      const seq = await prepBatchApi.buildSequence({ testMethodId: effTestMethodId, sort, tier, noRuns: 1, samples: toSort });
      const built = seq.map(seqRowToLocal).map(r => {
        const e = r.sampleLogInOid ? editByOid.get(r.sampleLogInOid) : undefined;
        const merged = e ? { ...r, ...e } : r;
        const isSample = merged.kind === 'sample';
        return {
          ...merged,
          prepMethod:      merged.prepMethod || commonPrepMethod,
          prepMethodOid:   merged.prepMethodOid ?? commonPrepMethodOid,
          units:           merged.units || commonUnits,
          holdTime:        merged.holdTime || commonHoldTime,
          functionType:    merged.functionType || commonFunctionType,
          functionTypeOid: merged.functionTypeOid ?? commonFunctionTypeOid,
          datePrepared:    merged.datePrepared ?? commonDatePrepared,
          prepOptions:     merged.prepOptions || (isSample ? '' : commonPrepOptions),
          prepOptionsOid:  merged.prepOptionsOid ?? (isSample ? null : commonPrepOptionsOid),
        };
      });
      setRows(renumber(built));
      setSorted(true);
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to build the prep sequence.'));
    }
  }

  // Previous ← step back from the built sequence to sample selection (re-pull).
  function goPrevious() { void loadSamples(); }

  // Manually add a QC row under the one checked parent row.
  async function addQc(q: QcTypeOption) {
    const selected = rows.filter(r => r.checked);
    if (selected.length > 1) { notify.warning('Select only one check box.'); return; }
    if (selected.length === 0) { notify.warning('Select a row first, then click ＋ to add QC under it.'); return; }
    const parent = selected[0];
    if (!parent) return;
    try {
      const seq = await prepBatchApi.addQc({
        testMethodId: effTestMethodId, parentSort: parent.sort ?? 0,
        newQcTypeOid: q.qcTypeOid, sequence: rows.map(toWireRow),
      });
      setRows(renumber(seq.map(seqRowToLocal)));
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to add QC.'));
    }
  }

  // QC Remove — drop a SINGLE row via its in-grid − button (QC Batch form parity,
  // QcBatchDetailPage.removeRow; the server renumbers via QcSequenceBuilder.RemoveQc).
  // Replaces the old top-toolbar "QC Remove" (checked-rows) button — the remove
  // control now lives inside the grid like the QC form (user request 2026-07-14).
  async function removeRow(id: string) {
    const target = rows.find(r => r.id === id);
    if (!target || target.sort == null) return;
    try {
      const seq = await prepBatchApi.removeQc({ testMethodId: effTestMethodId, sequence: rows.map(toWireRow), removeSorts: [target.sort] });
      setRows(renumber(seq.map(seqRowToLocal)));
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to remove the row.'));
    }
  }

  // ── drag & drop reorder (post-Sort) — QC Batch parity (QcBatchDetailPage). The
  // server (QcSequenceBuilder.MoveRow) validates the drop against the row's
  // relationships and returns the resequenced list; a disallowed drop warns and
  // the order is left unchanged. Pre-Sort the order is driven by the checkboxes. ──
  const dragId = useRef<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  async function onDrop(targetId: string) {
    const from = dragId.current;
    setDropTarget(null);
    dragId.current = null;
    if (!from || from === targetId) return;
    const dragRow = rows.find(r => r.id === from);
    const targetRow = rows.find(r => r.id === targetId);
    if (!dragRow || !targetRow || dragRow.sort == null || targetRow.sort == null) return;
    try {
      const seq = await prepBatchApi.moveRow({
        testMethodId: effTestMethodId, dragSort: dragRow.sort, targetSort: targetRow.sort,
        sequence: rows.map(toWireRow),
      });
      setRows(renumber(seq.map(seqRowToLocal)));
    } catch (e) {
      notify.warning(apiErrMsg(e, 'That move is not allowed.'));
    }
  }

  // ── right-click context menu (QC Batch parity — OG SamplePreparationController
  // Grid_FillContextMenuItems :2847 + ContextMenuItemClick :2197): "Copy To All
  // Cell" (always) and "Add Dilution" (post-Sort only, :2865). ──
  const [ctx, setCtx] = useState<{ x: number; y: number; row: PrepRow } | null>(null);
  function closeCtx() { setCtx(null); }
  // Last-focused copyable cell (OG allow-list — SampleAmount / FinalVolume).
  const [focusedCol, setFocusedCol] = useState<'sampleAmount' | 'finalVol' | null>(null);

  // "Copy To All Cell" — copies the right-clicked row's value for the last-focused
  // editable column (SampleAmount / FinalVol) to every CHECKED row (OG :2203).
  function copyToAll(row: PrepRow) {
    closeCtx();
    if (!focusedCol) return;
    const selected = rows.filter(r => r.checked);
    if (selected.length === 0) { notify.warning('Select at least one row to copy to.'); return; }
    const value = row[focusedCol];
    const ids = new Set(selected.map(r => r.id));
    setRows(prev => prev.map(r => (ids.has(r.id) ? { ...r, [focusedCol]: value } : r)));
    prepMsg(`Copied ${focusedCol === 'finalVol' ? 'FinalVol' : 'SampleAmount'} to ${selected.length} selected row${selected.length > 1 ? 's' : ''}.`, 'Copied');
  }

  // "Add Dilution" — mints one "<parentCode>R<n>" replicate under the single
  // CHECKED row via the shared engine (OG :2197). Post-Sort only.
  async function addDilution() {
    closeCtx();
    const selected = rows.filter(r => r.checked);
    if (selected.length > 1) { notify.warning('Select only one check box.'); return; }
    if (selected.length === 0) { notify.warning('Select a row first, then right-click to Add Dilution.'); return; }
    const parent = selected[0];
    if (!parent || parent.sort == null) return;
    if (parent.kind === 'dilution') { notify.warning('Cannot add a dilution to a dilution row.'); return; }
    try {
      const seq = await prepBatchApi.addDilution({
        testMethodId: effTestMethodId, parentSort: parent.sort, sequence: rows.map(toWireRow),
      });
      setRows(renumber(seq.map(seqRowToLocal)));
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to add dilution.'));
    }
  }

  // Pre-Sort: tick a sample row to assign it an incremental (1-based) Sort;
  // unticking renumbers the rest so the values stay contiguous.
  function toggleSeqRow(id: string) {
    setRows(prev => {
      const r = prev.find(x => x.id === id);
      if (!r) return prev;
      if (r.checked) {
        const oldSort = r.sort ?? 0;
        return prev.map(x =>
          x.id === id                          ? { ...x, checked: false, sort: 0 }
          : (x.sort != null && x.sort > oldSort) ? { ...x, sort: x.sort - 1 }
          : x);
      }
      const max = prev.reduce((m, x) => x.checked && x.sort != null && x.sort > m ? x.sort : m, 0);
      return prev.map(x => x.id === id ? { ...x, checked: true, sort: max + 1 } : x);
    });
  }
  function toggleAllSeq(on: boolean) {
    setRows(prev => {
      if (!on) return prev.map(r => ({ ...r, checked: false, sort: 0 }));
      let next = 0;
      return prev.map(r => ({ ...r, checked: true, sort: ++next }));
    });
  }
  // Post-Sort the checkbox is a plain multi-select (for Add QC / QC Remove).
  function toggleSeqRowChecked(id: string) {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, checked: !r.checked } : r)));
  }
  function toggleAllChecked(on: boolean) {
    setRows(prev => prev.map(r => ({ ...r, checked: on })));
  }

  // ── Others tab: Reagents (legacy ReagentLink_Execute parity) ───────────────
  // Loads once a batch is saved; refetched after every link/unlink.
  async function loadReagents(prepOid: string) {
    try {
      setReagentRows(await prepBatchApi.linkedReagents(prepOid));
    } catch {
      notify.error('Could not load linked reagents.');
    }
  }
  useEffect(() => { if (savedPrepId) void loadReagents(savedPrepId); }, [savedPrepId]);

  // Reagent picker OK — reconcile the picker's final staged set against current
  // state. Saved batch: delta link/unlink then refetch; pre-save: replace the
  // local stagedReagents (catalog rows carried through so Create ships them).
  async function reconcileReagents(final: StagedReagent[]) {
    if (!savedPrepId) {
      const prevByKey = new Map(stagedReagents.map(r => [r.reagentID ?? '', r] as const));
      const next: QcAvailableReagent[] = final.map(s =>
        s.catalog ?? prevByKey.get(s.reagentID) ?? {
          reagentID: s.reagentID, reagentName: s.reagentName, prepType: s.prepType, expiryDate: s.expiryDate,
          test: null, datePrepared: null, preparedBy: null, division: null, department: null,
          createdDate: null, dateReceived: null, vendor: null, vendorLT: null,
        });
      setStagedReagents(next);
      const added = final.filter(s => !prevByKey.has(s.reagentID)).length;
      const removed = stagedReagents.filter(r => !final.some(s => s.reagentID === r.reagentID)).length;
      if (added || removed) prepMsg(`${next.length} reagent(s) on the batch.`, 'Reagents');
      return;
    }
    const keptOids = new Set(final.map(s => s.linkedOid).filter((v): v is string => !!v));
    const toRemove = reagentRows.filter(r => !keptOids.has(r.oid));
    const toAdd    = final.filter(s => !s.linkedOid && s.catalog).map(s => s.catalog!);
    if (toRemove.length === 0 && toAdd.length === 0) return;
    try {
      for (const r of toRemove) await prepBatchApi.unlinkReagent(savedPrepId, r.oid, r.reagentID);
      for (const c of toAdd)    await prepBatchApi.linkReagent  (savedPrepId, c);
      await loadReagents(savedPrepId);
      setTabsDirty(true);   // Others-tab edit counts toward the OK dirty-gate (QC parity)
      const parts = [
        toAdd.length    ? `${toAdd.length} added`    : '',
        toRemove.length ? `${toRemove.length} removed` : '',
      ].filter(Boolean).join(', ');
      prepMsg(`Reagents updated — ${parts}.`, 'Reagents');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Updating reagents failed.'));
    }
  }

  async function unlinkSelectedReagents() {
    if (!savedPrepId) {
      if (stagedReagentSel.size === 0) { notify.warning('Select a reagent first.'); return; }
      setStagedReagents(prev => prev.filter(r => !stagedReagentSel.has(r.reagentID ?? '')));
      setStagedReagentSel(new Set());
      return;
    }
    if (reagentSel.size === 0) { notify.warning('Select a reagent first.'); return; }
    try {
      for (const oid of reagentSel) {
        const row = reagentRows.find(r => r.oid === oid);
        await prepBatchApi.unlinkReagent(savedPrepId, oid, row?.reagentID ?? null);
      }
      setReagentSel(new Set());
      await loadReagents(savedPrepId);
      prepMsg('Reagent(s) unlinked.', 'Reagents');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Unlinking reagent failed.'));
    }
  }
  const toggleReagentSel = (oid: string) =>
    setReagentSel(prev => { const n = new Set(prev); if (n.has(oid)) n.delete(oid); else n.add(oid); return n; });
  const toggleStagedReagentSel = (reagentId: string) =>
    setStagedReagentSel(prev => { const n = new Set(prev); if (n.has(reagentId)) n.delete(reagentId); else n.add(reagentId); return n; });
  const toggleAllReagentSel = (on: boolean) =>
    savedPrepId
      ? setReagentSel(on ? new Set(reagentRows.map(r => r.oid)) : new Set())
      : setStagedReagentSel(on ? new Set(stagedReagents.map(r => r.reagentID ?? '')) : new Set());
  const allReagentChecked = savedPrepId
    ? reagentRows.length > 0 && reagentRows.every(r => reagentSel.has(r.oid))
    : stagedReagents.length > 0 && stagedReagents.every(r => stagedReagentSel.has(r.reagentID ?? ''));
  // COA link — reagent-scoped, only when the row's server-computed hasCoa is true.
  // Reuses the QC reagent COA reader (no prep-specific COA endpoint exists).
  const openCoa = async (reagentId: string | null) => {
    if (!reagentId) return;
    try {
      await qcBatchApi.openReagentCoa(reagentId);
    } catch {
      notify.warning('No certificate on file for this reagent.');
    }
  };

  // ── Others tab: Instruments (legacy InstrumentLink_Execute parity) ─────────
  async function loadLinkedInstruments(prepOid: string) {
    try {
      setInstrumentRows(await prepBatchApi.linkedInstruments(prepOid));
    } catch {
      notify.error('Could not load linked instruments.');
    }
  }
  useEffect(() => { if (savedPrepId) void loadLinkedInstruments(savedPrepId); }, [savedPrepId]);

  async function reconcileInstruments(final: QcInstrument[]) {
    if (!savedPrepId) {
      const dedupByOid = new Map<string, QcInstrument>();
      for (const i of final) dedupByOid.set(i.instrumentOid, i);
      const next = Array.from(dedupByOid.values());
      const added = final.filter(i => !stagedInstruments.some(s => s.instrumentOid === i.instrumentOid)).length;
      const removed = stagedInstruments.filter(i => !final.some(s => s.instrumentOid === i.instrumentOid)).length;
      setStagedInstruments(next);
      if (added || removed) prepMsg(`${next.length} instrument(s) on the batch.`, 'Instruments');
      return;
    }
    const preOids = new Set(instrumentRows.map(i => i.instrumentOid));
    const finalOids = new Set(final.map(i => i.instrumentOid));
    const toRemove = instrumentRows.filter(i => !finalOids.has(i.instrumentOid));
    const toAdd    = final.filter(i => !preOids.has(i.instrumentOid));
    if (toRemove.length === 0 && toAdd.length === 0) return;
    try {
      for (const i of toRemove) await prepBatchApi.unlinkInstrument(savedPrepId, i.instrumentOid, i.instrumentName);
      for (const i of toAdd)    await prepBatchApi.linkInstrument  (savedPrepId, i.instrumentOid, i.instrumentName);
      await loadLinkedInstruments(savedPrepId);
      setTabsDirty(true);   // Others-tab edit counts toward the OK dirty-gate (QC parity)
      const parts = [
        toAdd.length    ? `${toAdd.length} added`    : '',
        toRemove.length ? `${toRemove.length} removed` : '',
      ].filter(Boolean).join(', ');
      prepMsg(`Instruments updated — ${parts}.`, 'Instruments');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Updating instruments failed.'));
    }
  }

  async function unlinkSelectedInstruments() {
    if (!savedPrepId) {
      if (stagedInstrumentSel.size === 0) { notify.warning('Select an instrument first.'); return; }
      setStagedInstruments(prev => prev.filter(i => !stagedInstrumentSel.has(i.instrumentOid)));
      setStagedInstrumentSel(new Set());
      return;
    }
    if (instrumentSel.size === 0) { notify.warning('Select an instrument first.'); return; }
    try {
      for (const oid of instrumentSel) {
        const row = instrumentRows.find(r => r.instrumentOid === oid);
        await prepBatchApi.unlinkInstrument(savedPrepId, oid, row?.instrumentName ?? null);
      }
      setInstrumentSel(new Set());
      await loadLinkedInstruments(savedPrepId);
      prepMsg('Instrument(s) unlinked.', 'Instruments');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Unlinking instrument failed.'));
    }
  }
  const toggleInstrumentSel = (oid: string) =>
    setInstrumentSel(prev => { const n = new Set(prev); if (n.has(oid)) n.delete(oid); else n.add(oid); return n; });
  const toggleStagedInstrumentSel = (oid: string) =>
    setStagedInstrumentSel(prev => { const n = new Set(prev); if (n.has(oid)) n.delete(oid); else n.add(oid); return n; });
  const toggleAllInstrumentSel = (on: boolean) =>
    savedPrepId
      ? setInstrumentSel(on ? new Set(instrumentRows.map(i => i.instrumentOid)) : new Set())
      : setStagedInstrumentSel(on ? new Set(stagedInstruments.map(i => i.instrumentOid)) : new Set());
  const allInstrumentChecked = savedPrepId
    ? instrumentRows.length > 0 && instrumentRows.every(i => instrumentSel.has(i.instrumentOid))
    : stagedInstruments.length > 0 && stagedInstruments.every(i => stagedInstrumentSel.has(i.instrumentOid));

  // ── Copy From Previous Batch (prep twin of QcBatchDetailPage.copyFromPrevious) ──
  // OG SamplePreparationController.btnCopyFromSamplePrepAction_Execute (:6125) +
  // CopyingFromPreviousBatch (:6173): checkboxes Reagent · Instrument · Others
  // (=Comment) · All the Above. Reuses the same add paths the pickers use (link on
  // a saved batch, stage otherwise). Sequence / Temperature / Humidity are NOT
  // copied (legacy doesn't either).
  async function copyFromPrevious(sourcePrepOid: string, copyReagents: boolean, copyInstruments: boolean, copyComment: boolean) {
    try {
      let addedR = 0, addedI = 0; let comm = false;
      if (copyComment) {
        // OG "Others" — curBatch.Comments = source.Comments: REPLACE this batch's
        // comment with the source's.
        const detail = await prepBatchApi.getDetail(sourcePrepOid);
        setComment(detail.header.comments ?? '');
        comm = true;
      }
      if (copyReagents) {
        const src = await prepBatchApi.linkedReagents(sourcePrepOid);
        const toAvail = (r: QcLinkedReagent): QcAvailableReagent => ({
          reagentID: r.reagentID, reagentName: r.reagentName, prepType: r.prepType, expiryDate: r.expiredDate,
          test: null, datePrepared: r.datePrepared, preparedBy: r.preparedBy, division: null, department: null,
          createdDate: null, dateReceived: r.dateReceived, vendor: r.vendorName, vendorLT: r.vendorLT,
        });
        if (savedPrepId) {
          const have = new Set(reagentRows.map(r => r.reagentID));
          for (const r of src) {
            if (!r.reagentID || have.has(r.reagentID)) continue;
            await prepBatchApi.linkReagent(savedPrepId, toAvail(r)); addedR++;
          }
          if (addedR) await loadReagents(savedPrepId);
        } else {
          const have = new Set(stagedReagents.map(r => r.reagentID));
          const add = src.filter(r => r.reagentID && !have.has(r.reagentID)).map(toAvail);
          addedR = add.length;
          if (add.length) setStagedReagents(prev => [...prev, ...add]);
        }
      }
      if (copyInstruments) {
        const src = await prepBatchApi.linkedInstruments(sourcePrepOid);
        if (savedPrepId) {
          const have = new Set(instrumentRows.map(i => i.instrumentOid));
          for (const i of src) {
            if (have.has(i.instrumentOid)) continue;
            await prepBatchApi.linkInstrument(savedPrepId, i.instrumentOid, i.instrumentName); addedI++;
          }
          if (addedI) await loadLinkedInstruments(savedPrepId);
        } else {
          const have = new Set(stagedInstruments.map(i => i.instrumentOid));
          const add = src.filter(i => !have.has(i.instrumentOid));
          addedI = add.length;
          if (add.length) setStagedInstruments(prev => [...prev, ...add]);
        }
      }
      const parts: string[] = [];
      if (copyReagents)    parts.push(`${addedR} reagent(s)`);
      if (copyInstruments) parts.push(`${addedI} instrument(s)`);
      if (comm)            parts.push('the comment');
      prepMsg(`Copied ${parts.join(', ')} from the previous batch.`, 'Copy From Previous');
    } catch (e) {
      notify.error(apiErrMsg(e, 'Copy from previous batch failed.'));
    }
  }

  // OK → persist the batch (mint race-safe PrepBatchID, link samples) via the
  // shared save flow ("Do you want to Save?" → save → "Saved" → back to queue).
  function onOk() {
    if (viewOnly) return;   // read-only (no write permission / completed batch) — never saves
    if (!sorted) { notify.warning('Sort the sequence before saving.'); return; }
    const jobIdsLabel = jobsList.filter(j => jobOids.includes(j.jobOid)).map(j => j.jobID).filter(Boolean).join(';');
    const instr = instrList.find(i => i.instrumentOid === instrument);
    const sequence: PrepSeqRow[] = rows.map(toWireRow);
    // Captured inside `save` so the milestone-success MODAL (§13) can show the
    // minted Prep Batch ID + detail rows after the record is created (user 2026-07-17
    // — the created batch ID must appear in a dialog, not a top toast).
    let minted: PrepSaveResult | null = null;
    void runSave({
      save: () => prepBatchApi.save({
        testMethodId:   effTestMethodId,
        testMethodIds:  effTestMethodIds,
        matrixId:       seed.matrixId ?? null,
        methodId:       seed.methodId ?? null,
        tier,
        sort,
        jobIds:         jobIdsLabel || null,
        instrumentOid:  instrument,
        instrumentName: instr?.instrumentName ?? null,
        temperature:    temperature || null,
        humidity:       humidity || null,
        comments:       comment.trim() || null,
        sequence,
        // Others-tab reagents/instruments staged before the batch existed —
        // persisted atomically with Create (mirrors the QC twin).
        stagedReagents: stagedReagents.length ? stagedReagents.map(r => ({
          reagentId: r.reagentID ?? '', reagentName: r.reagentName ?? '', prepType: r.prepType,
          expiredDate: r.expiryDate, vendorLT: r.vendorLT, preparedBy: r.preparedBy,
          datePrepared: r.datePrepared, dateReceived: r.dateReceived, vendorOid: null,
        })) : undefined,
        stagedInstrumentOids: stagedInstruments.length ? stagedInstruments.map(i => i.instrumentOid) : undefined,
      }).then(result => { minted = result; setSavedPrepId(result.prepOid); }),
      // §13 milestone-success MODAL (a new prep batch + ID was created) — green,
      // big Prep Batch ID + detail rows, replacing the plain top toast.
      successDialog: () => ({
        type: 'success',
        title: 'Sample Prep Batch Created',
        idLabel: 'Prep Batch ID',
        idValue: minted?.prepBatchId ?? '',
        rows: [
          { label: 'Test', value: effTest || '—' },
          { label: 'Job(s)', value: jobIdsLabel || '—' },
          { label: 'Runs', value: String(minted?.seqCount ?? sequence.length) },
          { label: 'Samples linked', value: String(minted?.samplesLinked ?? 0) },
        ],
        note: 'Returning to the Sample Prep Queue.',
      }),
      onSaved: onBack,
      formName: 'Sample Prep Batches',
      auditAction: 'create',
      onError: (e) => notify.error(apiErrMsg(e, 'Save failed. Please try again.')),
    });
  }

  // Save (edit mode) → A1 reconcile-on-Save: persist the current grid + header
  // Temperature/Humidity/Comments/Instrument back to the saved batch via the shared
  // save flow ("Do you want to Save?" → PUT → "Saved successfully."), then reload
  // from the server (fresh seqOids for added rows, removed rows gone) and stay in
  // edit mode. Twin of QcBatchDetailPage.saveEdits.
  function saveEdits() {
    if (!existingHeader) return;
    const instr = instrList.find(i => i.instrumentOid === instrument);
    const sequence: PrepSeqRow[] = rows.map(toWireRow);
    void runSave({
      save: () => prepBatchApi.updateBatch(existingHeader.prepBatchOid, {
        temperature:    temperature || null,
        humidity:       humidity || null,
        comments:       comment.trim() || null,
        instrumentOid:  instrument,
        instrumentName: instr?.instrumentName ?? existingHeader.instrumentName ?? null,
        sequence,
      }).then(() => loadDetail(existingHeader.prepBatchOid)),
      successMessage: 'Saved successfully.',
      successAs: 'dialog',   // shared success MODAL (user 2026-07-17 — consistent with the create dialog)
      onSaved: onBack,
      formName: 'Sample Prep Batches',
      auditAction: 'edit',
      onError: (e) => notify.error(apiErrMsg(e, 'Save failed. Please try again.')),
    });
  }

  // ── toolbar / gating ──
  const canNotes = jobOids.length > 0 || savedPrepId !== null;
  const anySample = rows.some(r => r.kind === 'sample');
  const allChecked = rows.length > 0 && rows.every(r => r.checked);
  const rowClass = (r: PrepRow) => (r.kind === 'sample' ? 'sample' : r.sourceId ? 'child' : 'qc');

  // Export To QC Batch (OG SamplePreparationController.btnExportToQCBatch_Execute).
  // Enabled only on a SAVED batch (OG disables it for a new/unsaved prep batch,
  // ctrl :1657-1664). Validate in OG's message order — status gates first, then
  // the already-in-a-QC-batch guard — then hand the batch to the parent, which
  // swaps to the QC Batch form seeded to auto-import these prepared samples.
  async function runExportToQc() {
    if (!existingHeader || !onExportToQc) return;
    const h = existingHeader;
    // OG btnExportToQCBatch_Execute (:125-130): a multi-test Tier-1 prep batch cannot
    // be exported — a QC batch is single-test. Block it FIRST, matching OG's order.
    if ((h.testMethodId ?? '').split(';').map(s => s.trim()).filter(Boolean).length > 1) {
      void showDialog({ type: 'error', title: 'Export To QC Batch',
        message: 'Cannot export multiple tests in the same QC batch.' });
      return;
    }
    // PrepBatchQueueStatus: 1 Pending Tier2, 2 Pending Result Entry, 3 Result Completed.
    if (h.status === 3) {
      void showDialog({ type: 'error', title: 'Export To QC Batch',
        message: 'Results already entered for this batch.' });
      return;
    }
    if (h.status === 1) {
      void showDialog({ type: 'error', title: 'Export To QC Batch',
        message: 'You need to complete Preparation Tier 2.' });
      return;
    }
    if (h.status !== 2) {
      void showDialog({ type: 'warning', title: 'Export To QC Batch',
        message: 'Complete the sample preparation before exporting to a QC batch.' });
      return;
    }
    try {
      const { hasQcBatch } = await prepBatchApi.exportCheck(h.prepBatchOid);
      if (hasQcBatch) {
        void showDialog({ type: 'error', title: 'Export To QC Batch',
          message: 'A QC Batch ID has already been created for this Job ID.' });
        return;
      }
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to check the prep batch before export.'));
      return;
    }
    onExportToQc({
      prepOid:      h.prepBatchOid,
      prepBatchId:  h.prepBatchId ?? '',
      testMethodId: h.testMethodId,
      test:         h.test,
      method:       h.method,
      matrix:       h.matrix,
    });
  }

  const rootCls = ['qcb-root', giCollapsed ? 'geninfo-collapsed' : '', qcCollapsed ? 'qc-collapsed' : '', fullGrid ? 'fullgrid' : '']
    .filter(Boolean).join(' ');

  return (
    <>
      <WorkflowStepperBar stepStatus={stepStatus} steps={PREP_STEPS} />
      <div className={rootCls} onClick={() => { setOpenPop(null); closeCtx(); }}>
        <div className="crumb">
          <a onClick={onBack}>Sample Prep Queue</a> / <b>Sample Prep Batch</b>
        </div>
        <div className="pagehead">
          <FormTitle icon="ti-flask" title="Sample Prep Batch"
            auditFormName="Sample Prep Batches" auditSourceId={savedPrepId ?? undefined} />
        </div>

        <div className={`hint ${sorted ? 'ok' : ''}`}>
          {existingHeader
            ? (viewOnly
                ? <span><b>Viewing Sample Prep Batch {existingHeader.prepBatchId}</b> — read-only (results completed).</span>
                : <span><b>Editing Sample Prep Batch {existingHeader.prepBatchId}.</b> Edit the run cells, drag to reorder, QC <b>＋</b> / row <b>−</b> to add/remove, check a row then right-click for <b>Add Dilution</b>, then click <b>OK</b> to save.</span>)
            : viewOnly
            ? <span><b>Read-only.</b> You don't have write permission for Sample Preparation, so this form can be viewed but not saved.</span>
            : sorted
            ? <span><b>Run sequence built — {rows.length} runs.</b> Drag to reorder, QC <b>＋</b> / row <b>−</b> to add/remove, check a row then right-click for <b>Add Dilution</b>. Click <b>OK</b> to create the prep batch.</span>
            : <span><b>Step 3.</b> Pick the <b>Job ID(s)</b> and an <b>Instrument</b>, then click <b>Load</b>, then <b>Sort</b> to build the run sequence.</span>}
        </div>

        {/* Shared <AppToolbar> — form-specific buttons in the canonical order.
            Reset · Load sit at the far right; OK is the primary commit below.
            NO Refresh here (onRefresh omitted): in the detail view it re-ran the
            pending-samples load, which clears the grid for a SAVED batch (its samples
            are off the pending queue). The create flow uses Reset · Load; QC's detail
            has no Refresh either. (user 2026-07-17) */}
        <div onClick={e => e.stopPropagation()}>
          <AppToolbar
            left={
              <>
                {/* OG parity (SamplePreparationController :559 —
                    DeleteAction.TargetObjectsCriteria = "PrepBatchStatus <> ResultCompleted"):
                    Delete is gated ONLY on status, NOT on view/edit mode, so it shows
                    in VIEW mode too. Hidden only for a ResultCompleted (status 3) batch.
                    QC Remove is NOT a toolbar button; it's the per-row − in the grid.
                    (user 2026-07-17 — enable Delete in view mode.) */}
                {existingHeader && existingHeader.status !== 3 && (
                  <button className="ca-btn" title="Delete"
                    onClick={() => void runDelete({
                      formName: 'Sample Prep Batches',
                      message: `Delete Prep Batch ${existingHeader.prepBatchId ?? ''}? Its samples return to the prep queue and this cannot be undone.`,
                      delete: () => prepBatchApi.deleteBatch(existingHeader.prepBatchOid).then(() => undefined),
                      onDeleted: onBack,
                      onError: (e) => notify.error(apiErrMsg(e, 'Deleting the prep batch failed.')),
                    })}><i className="ti ti-trash" /> Delete</button>
                )}
                {/* Add Sample (OG AddSample_Execute) — appends more pending-prep
                    candidates to a SAVED batch's sequence. OG activates it ONLY on the
                    existing-batch edit view (SamplePrepBatch_DetailView_Copy_History,
                    ViewEditMode==Edit && SamplepreparationWrite — SamplePreparationController
                    :2530-2542), NEVER on the new-batch build view. So it shows only for an
                    editable EXISTING batch, not during the Job→Load→Sort→OK create flow.
                    (fix 2026-07-16 — was `!viewOnly && loaded`, which wrongly showed it
                    after Load on a new batch once existing-batch editing landed.) */}
                {existingHeader && !viewOnly && (
                  <button className="ca-btn" title="Add Sample"
                    onClick={() => void openAddSample()}><i className="ti ti-plus" /> Add Sample</button>
                )}
                {/* Project Notes stays available in VIEW mode (viewing notes is a
                    read action). Copy From Previous MUTATES the current batch, so it
                    is EDIT-mode only (hidden in view), like Add Sample / Delete. */}
                <button className="ca-btn" disabled={!canNotes} title={canNotes ? '' : 'Select a job first'}
                  onClick={() => setModal('notes')}><i className="ti ti-notes" /> Project Notes{notesCount != null ? ` (${notesCount})` : ''}</button>
                {!viewOnly && (
                  <button className="ca-btn" disabled={!loaded}
                    onClick={() => setModal('copy')}><i className="ti ti-copy" /> Copy From Previous Batch</button>
                )}
                {showExportToQc && existingHeader && onExportToQc && (
                  <button className="ca-btn"
                    title="Export this prep batch's samples to a new QC Batch"
                    onClick={() => void runExportToQc()}><i className="ti ti-table-export" /> Export To QC Batch</button>
                )}
              </>
            }
            right={
              <>
                {!existingHeader && (
                  <button className="ca-btn" style={{ marginLeft: 'auto' }} onClick={resetBatch}>
                    <i className="ti ti-rotate" /> Reset</button>
                )}
                {!existingHeader && (
                  <button className="ca-btn ca-btn-solid" disabled={!loadEnabled || sorted} onClick={() => loadSamples()}>
                    <span aria-hidden="true">⬇️</span>&nbsp;Load</button>
                )}
              </>
            }
          />
        </div>

        {/* detail card */}
        <div className="card detailcard" onClick={e => { e.stopPropagation(); setOpenPop(null); closeCtx(); }}>
          <div className="cardhd">
            <div className="ttl"><span className="bar" /> General Information</div>
            <div className="sp" />
            <button className="iconbtn" title="Collapse / expand details" onClick={() => setGiCollapsed(v => !v)}>{giCollapsed ? '▾' : '▴'}</button>
            <button className="iconbtn" title="Hide / show QC Type panel" onClick={() => setQcCollapsed(v => !v)}>◧</button>
            <button className="iconbtn" title="Full screen grid" onClick={() => setFullGrid(v => !v)}>{fullGrid ? '⤡' : '⛶'}</button>
          </div>

          <div className="geninfo">
            <div className="form-grid">
              <Fld label="Prep Batch ID:"><input value={existingHeader?.prepBatchId ?? 'Autogenerate'} readOnly /></Fld>
              <Fld label="Test:" req><input value={effTest ?? ''} readOnly /></Fld>
              <Fld label="Tier:"><input value={String(tier)} readOnly /></Fld>

              <Fld label="Date Created:"><input
                value={fmtLocalDate(existingHeader?.createdDate ?? new Date().toISOString())} readOnly /></Fld>
              <Fld label="Method:" req><input value={effMethod ?? ''} readOnly /></Fld>
              <Fld label="Humidity(%):" yellow><input value={humidity} disabled={headerLocked} onChange={e => setHumidity(e.target.value)} /></Fld>

              <Fld label="Created By:" yellow><input value={existingHeader?.createdByName ?? 'Service'} readOnly /></Fld>
              <PickerFld label="Job ID:" req open={openPop === 'job'} disabled={sorted || viewOnly}
                onToggle={() => setOpenPop(openPop === 'job' ? null : 'job')}
                text={jobLabel} placeholder={!jobOids.length}>
                {openPop === 'job' && (
                  <div className="pop">
                    <div className="popscroll"><table>
                      <thead><tr><th className="cbcol">#</th><th>Job ID</th><th className="num">Sx</th><th>TAT</th><th>Due Date</th><th>Date Received</th></tr></thead>
                      <tbody>
                        {jobsList.length === 0 && <tr><td colSpan={6} className="nodata">No jobs with pending samples.</td></tr>}
                        {jobsList.map(j => (
                          <tr key={j.jobOid} className={jobOids.includes(j.jobOid) ? 'on' : ''} onClick={() => toggleJob(j.jobOid)}>
                            <td className="cbcol"><input type="checkbox" className="chk" readOnly checked={jobOids.includes(j.jobOid)} /></td>
                            <td>{j.jobID}</td>
                            <td className="num">{j.sampleCount}</td>
                            <td>{j.tat ?? ''}</td>
                            <td>{fmtDate(j.dueDate)}</td>
                            <td>{fmtDate(j.receivedDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                    <div className="pfoot"><span>Page 1 of 1 ({jobsList.length} items)</span>
                      <button className="apply" onClick={() => setOpenPop(null)}>Apply</button></div>
                  </div>
                )}
              </PickerFld>
              <Fld label="Temp(°C):" yellow><input value={temperature} disabled={headerLocked} onChange={e => setTemperature(e.target.value)} /></Fld>

              <Fld label="Matrix:" req><input value={effMatrix ?? ''} readOnly /></Fld>
              <PickerFld label="Instrument:" req open={openPop === 'instr'} disabled={sorted || viewOnly}
                onToggle={() => setOpenPop(openPop === 'instr' ? null : 'instr')}
                text={instrLabel} placeholder={!instrument}>
                {openPop === 'instr' && (
                  <div className="pop">
                    <div className="popscroll"><table>
                      <thead><tr><th className="cbcol">#</th><th>InstrumentNumber</th><th>InstrumentName</th></tr></thead>
                      <tbody>
                        {instrList.length === 0 && <tr><td colSpan={3} className="nodata">No instruments linked to this test.</td></tr>}
                        {instrList.map(ins => (
                          <tr key={ins.instrumentOid} className={instrument === ins.instrumentOid ? 'on' : ''} onClick={() => pickInstr(ins.instrumentOid)}>
                            <td className="cbcol"><input type="checkbox" className="chk" readOnly checked={instrument === ins.instrumentOid} /></td>
                            <td>{ins.labwareName}</td>
                            <td>{ins.instrumentName}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                    <div className="pfoot"><span>Page 1 of 1 ({instrList.length} items)</span>
                      <button className="apply" onClick={() => setOpenPop(null)}>Apply</button></div>
                  </div>
                )}
              </PickerFld>
            </div>
          </div>

          {/* tabs */}
          <div className="tabsrow">
            <div className="tabs">
              <div className={`tab ${tab === 'seq' ? 'active' : ''}`} onClick={() => setTab('seq')}>Batch Sequence</div>
              <div className={`tab ${tab === 'others' ? 'active' : ''}`} onClick={() => setTab('others')}>Others</div>
            </div>
            <div className="gridctl">
              <button className="btn" onClick={() => setGiCollapsed(v => !v)}>{giCollapsed ? '▾ Show details' : '▴ Collapse details'}</button>
              <button className="btn" onClick={() => setFullGrid(v => !v)}>{fullGrid ? '⤡ Exit full screen' : '⛶ Full screen'}</button>
            </div>
          </div>

          {tab === 'seq' ? (
            <div className="bseq">
              <div className="qcpanel">
                <div className="qh"><span>QCType</span><span>▲</span></div>
                <div className="qlist">
                  {loaded ? qcTypeList.filter(q => q.panelVisible).map(q => (
                    <div className="qitem" key={q.qcTypeOid}>
                      <span>{q.qcTypeName}</span>
                      {sorted && !viewOnly && <button className="circ add" title={`Add ${q.qcTypeName}`}
                        onClick={() => void addQc(q)}>＋</button>}
                    </div>
                  )) : (
                    <div className="nodata" style={{ padding: 18 }}>Load samples to see QC types.</div>
                  )}
                </div>
                <div className="qcount">Count={loaded ? qcTypeList.filter(q => q.panelVisible).length : 0}</div>
              </div>
              <div className={`seqwrap ${sorted ? '' : 'presort'} ${viewOnly ? 'viewonly' : ''}`}>
                {/* Grid toolbar strip — OG has the XAF default "Export to" dropdown
                    here, directly above the sequence grid's column headers. */}
                <div className="seqgridbar">
                  <ExportMenu filename={`prep-batch-sequence${existingHeader?.prepBatchId ? `-${existingHeader.prepBatchId}` : ''}`}
                              headers={seqExport.headers} rows={seqExport.rows} />
                </div>
                <div className="stscroll">
                  <table>
                    {/* Pin the 7 frozen leading columns to exact widths via <col>
                        (auto layout honours a column-level width even for the
                        narrow sticky Sort cell, where a cell min-width was ignored
                        and collapsed on scroll). The data columns get NO <col> →
                        content-sized, exactly like the QC form. (user 2026-07-14) */}
                    <colgroup>
                      <col className="c-drag" />
                      <col className="c-cb" />
                      <col className="c-rm" />
                      <col className="c-qct" />
                      <col className="c-sort" />
                      <col className="c-sys" />
                      <col className="c-job" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="cbcol frz fz0" />
                        <th className="cbcol frz fz1"><input type="checkbox" className="chk" checked={allChecked}
                          disabled={viewOnly}
                          onChange={e => (sorted ? toggleAllChecked(e.target.checked) : toggleAllSeq(e.target.checked))} /></th>
                        <th className="cbcol frz fz2" />
                        <th className="frz fz3">QCType</th>
                        <th className="num frz fz4">Sort</th>
                        {/* Frozen identity block matches the QC Batch form EXACTLY
                            (shared qcBatch.css offsets): SysSampleCode = fz5 (--w-sys)
                            then JobID = fz6 (--w-job).  DF is NON-frozen and sits AFTER
                            the frozen block (col-df), like QC — a non-frozen column can
                            never be wedged between two frozen ones or the sticky chain
                            breaks and the Sort column collapses on horizontal scroll
                            (user 2026-07-14). */}
                        <th className="frz fz5">SysSamplecode</th>
                        <th className="frz fz6">JobID</th>
                        {/* Pre-Sort shows the DilutionCount (its full caption needs the
                            wider col-num-sm); post-Sort it's the short "DF" (col-df).
                            Matches the QC Batch grid. (user 2026-07-14) */}
                        {/* Non-frozen DATA columns — array-driven from the column model
                            so Hide / Column Chooser drop them from BOTH header and body.
                            Each carries the shared .adg-resize handle (position:relative
                            anchors it). DupSource/SpikeSource keep .col-dupspike so they
                            still collapse pre-Sort via CSS. */}
                        {seqCm.visibleColumns.map(c => (
                          <th key={c.key}
                              className={seqHClass[c.key] ?? ''}
                              style={{ position: 'relative', ...seqW(c.key) }}
                              onContextMenu={(e) => seqMenu.openHeaderMenu(e, { field: c.key, label: c.label, sortable: false })}>
                            {c.label}
                            <span className="adg-resize" onMouseDown={(e) => seqCm.startResize(c.key, e)} onClick={(e) => e.stopPropagation()} />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr><td colSpan={7 + seqCm.visibleColumns.length} className="nodata">No data to display — pick Job ID + Instrument and click <b>Load</b>, then <b>Sort</b>.</td></tr>
                      ) : rows.map(r => (
                        <tr key={r.id}
                          className={`${rowClass(r)} ${r.checked ? 'sel' : ''} ${dropTarget === r.id ? 'dropbefore' : ''}`}
                          draggable={sorted && !viewOnly}
                          onDragStart={() => { if (sorted && !viewOnly) dragId.current = r.id; }}
                          onDragOver={e => { if (!sorted || viewOnly) return; e.preventDefault(); if (r.id !== dragId.current) setDropTarget(r.id); }}
                          onDrop={e => { if (!sorted || viewOnly) return; e.preventDefault(); void onDrop(r.id); }}
                          onDragEnd={() => setDropTarget(null)}
                          onClick={() => { if (sorted && !viewOnly) toggleSeqRowChecked(r.id); }}
                          onContextMenu={e => {
                            if (viewOnly) return;   // nothing mutating to offer in view mode
                            e.preventDefault();
                            setCtx({ x: Math.min(e.clientX, window.innerWidth - 190), y: Math.min(e.clientY, window.innerHeight - 120), row: r });
                          }}>
                          {/* Drag-to-reorder handle appears only AFTER Sort — pre-Sort the
                              run order is driven by the checkboxes, not dragging (QC parity). */}
                          <td className="drag frz fz0" title={sorted && !viewOnly ? 'Drag to reorder' : ''}>{sorted && !viewOnly ? '⠿' : ''}</td>
                          <td className="cbcol frz fz1"><input type="checkbox" className="chk"
                            checked={r.checked}
                            disabled={viewOnly}
                            onClick={e => e.stopPropagation()}
                            onChange={() => { if (sorted) toggleSeqRowChecked(r.id); else toggleSeqRow(r.id); }} /></td>
                          {/* Per-row QC Remove − (QC Batch form parity) — appears only
                              AFTER Sort, hidden in view mode. Replaces the old top-toolbar
                              "QC Remove" button (user 2026-07-14). */}
                          <td className="cbcol frz fz2">{sorted && !viewOnly && <button className="circ rm" title="Remove"
                            onClick={e => { e.stopPropagation(); void removeRow(r.id); }}>−</button>}</td>
                          <td className="qct frz fz3">{r.qc}</td>
                          <td className="num frz fz4">{r.sort ?? ''}</td>
                          <td className="frz fz5">{r.code || ''}</td>
                          {/* JobID shows only for a Sample-QCType row (OG parity) — QC
                              standard rows (CCB/ICV/LCS/…) leave it blank. */}
                          <td className="frz fz6">{r.qc === 'Sample' ? (r.job || '') : ''}</td>
                          {/* Pre-Sort EDITABLE prep cells — sample rows only; QC rows
                              stay blank/read-only. Post-Sort/viewOnly = read-only
                              display (unchanged). OG SamplePreparationController load
                              grid makes DilutionCount/SampleAmount/FinalVolume/
                              PrepNameOptions(combo)/DatePrepared editable. */}
                          {/* Pre-Sort: editable DilutionCount (sample rows). Post-Sort: editable
                              DF (all rows — QC parity, OG AllowEdit). Read-only in view mode. */}
                          {(() => {
                            // Body cells for the non-frozen DATA columns, keyed by id —
                            // same set + order as the header (visibleColumns) so a Hide
                            // drops the cell from both. seqW() applies a dragged width over
                            // the fixed CSS min/max. All the viewOnly/sorted/kind editable
                            // branches are unchanged, just re-homed into the map.
                            const cell: Record<string, ReactNode> = {
                              dilution: viewOnly ? (
                                <td key="dilution" className={`num ${sorted ? 'col-df' : 'col-num-sm'}`} style={seqW('dilution')}>{sorted ? (r.df || '') : (r.dilutionCount ?? '')}</td>
                              ) : sorted ? (
                                <td key="dilution" className="num col-df edcell" style={seqW('dilution')}><input className="cellinp num" type="number" min={1}
                                  value={r.df || ''}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => updateRow(r.id, { df: e.target.value === '' ? 0 : Math.max(0, Number(e.target.value) || 0) })}
                                  onBlur={e => updateRow(r.id, { df: Math.max(1, Number(e.target.value) || 1) })} /></td>
                              ) : r.kind === 'sample' ? (
                                <td key="dilution" className="num col-num-sm edcell" style={seqW('dilution')}>
                                  <DilutionCell value={r.dilutionCount ?? 1}
                                    onCommit={n => updateRow(r.id, { dilutionCount: n })} />
                                </td>
                              ) : (
                                <td key="dilution" className="num col-num-sm" style={seqW('dilution')}>{r.dilutionCount ?? ''}</td>
                              ),
                              // SampleAmount / FinalVol — editable pre- AND post-Sort (all rows); read-only in view mode.
                              sampleAmount: !viewOnly ? (
                                <td key="sampleAmount" className="num col-num-sm edcell" style={seqW('sampleAmount')}><input className="cellinp num"
                                  value={r.sampleAmount}
                                  onChange={e => updateRow(r.id, { sampleAmount: e.target.value })}
                                  onFocus={() => setFocusedCol('sampleAmount')}
                                  onClick={e => e.stopPropagation()} /></td>
                              ) : (
                                <td key="sampleAmount" className="num col-num-sm" style={seqW('sampleAmount')}>{r.sampleAmount || ''}</td>
                              ),
                              finalVol: !viewOnly ? (
                                <td key="finalVol" className="num edcell" style={seqW('finalVol')}><input className="cellinp num"
                                  value={r.finalVol}
                                  onChange={e => updateRow(r.id, { finalVol: e.target.value })}
                                  onFocus={() => setFocusedCol('finalVol')}
                                  onClick={e => e.stopPropagation()} /></td>
                              ) : (
                                <td key="finalVol" className="num" style={seqW('finalVol')}>{r.finalVol || ''}</td>
                              ),
                              // Dup/Spike SOURCE — selection combo on rows with valid
                              // candidates, plain text otherwise (twin of the QC form).
                              // Dropdown ONLY when there is a real choice (2+ candidates);
                              // one candidate renders as text, and no blank entry is
                              // offered. Twin of the QC form (user 2026-08-07).
                              dupSource: (
                                <td key="dupSource" className="col-dupspike edcell" style={seqW('dupSource')}>
                                  {sorted && !viewOnly && (sourceOpts.get(r.id)?.dup.length ?? 0) > 1
                                    ? <select className="cellinp" value={r.dupSource || ''}
                                        onClick={e => e.stopPropagation()}
                                        onChange={e => updateRow(r.id, { dupSource: e.target.value })}>
                                        {sourceOpts.get(r.id)!.dup.map(o => <option key={o} value={o}>{o}</option>)}
                                      </select>
                                    : (sorted ? (r.dupSource || '') : '')}
                                </td>
                              ),
                              spikeSource: (
                                <td key="spikeSource" className="col-dupspike edcell" style={seqW('spikeSource')}>
                                  {sorted && !viewOnly && (sourceOpts.get(r.id)?.spike.length ?? 0) > 1
                                    ? <select className="cellinp" value={r.spikeSource || ''}
                                        onClick={e => e.stopPropagation()}
                                        onChange={e => updateRow(r.id, { spikeSource: e.target.value })}>
                                        {sourceOpts.get(r.id)!.spike.map(o => <option key={o} value={o}>{o}</option>)}
                                      </select>
                                    : (sorted ? (r.spikeSource || '') : '')}
                                </td>
                              ),
                              sampleName:     <td key="sampleName" style={seqW('sampleName')}>{r.sampleName || ''}</td>,
                              siteName:       <td key="siteName" style={seqW('siteName')}>{r.siteName || ''}</td>,
                              samplingAddress:<td key="samplingAddress" style={seqW('samplingAddress')}>{r.samplingAddress || ''}</td>,
                              units:          <td key="units" style={seqW('units')}>{r.units || ''}</td>,
                              prepMethod:     <td key="prepMethod" style={seqW('prepMethod')}>{r.prepMethod || ''}</td>,
                              // NTU (turbidity) — editable numeric on every row, pre/post-Sort; read-only in
                              // view mode. Drives the computed SamplePrep column. (feature 2026-08-14)
                              ntu: !viewOnly ? (
                                <td key="ntu" className="num col-num-sm edcell" style={seqW('ntu')}><input className="cellinp num" type="number" step="any" min={0}
                                  value={r.ntu}
                                  onChange={e => updateRow(r.id, { ntu: e.target.value })}
                                  onClick={e => e.stopPropagation()} /></td>
                              ) : (
                                <td key="ntu" className="num col-num-sm" style={seqW('ntu')}>{r.ntu || ''}</td>
                              ),
                              // SamplePrep — READ-ONLY, computed from NTU by the ACT rule configured for this
                              // test/method (Settings → Workflow Configuration → ACT); blank until an NTU value
                              // is entered. Replaces the old PrepOptions column. (feature 2026-08-14)
                              samplePrep: (() => {
                                const sp = samplePrepFromNtu(r.ntu);
                                // Yes = red, No = green (user request 2026-08-14); blank stays uncoloured.
                                const spColor = sp === 'Yes' ? 'var(--ai-red)' : sp === 'No' ? 'var(--ai-green)' : undefined;
                                return <td key="samplePrep" style={{ ...seqW('samplePrep'), color: spColor, fontWeight: sp ? 600 : undefined }}>{sp}</td>;
                              })(),
                              // FunctionType (SamplePrepType) — READ-ONLY in both states.
                              functionType: <td key="functionType" style={seqW('functionType')}>{r.functionType || ''}</td>,
                              holdTime:     <td key="holdTime" style={seqW('holdTime')}>{r.holdTime || ''}</td>,
                              // DatePrepared — shared InlineCellEditor (datetime); editable on sample rows, read-only in view mode.
                              datePrepared: (
                                <td key="datePrepared" style={seqW('datePrepared')}>
                                  <InlineCellEditor value={r.datePrepared} type="datetime"
                                    disabled={viewOnly || r.kind !== 'sample'}
                                    onCommit={wire => updateRow(r.id, { datePrepared: wire })} />
                                </td>
                              ),
                              dateReceived:  <td key="dateReceived" style={seqW('dateReceived')}>{fmtLocalShort(r.dateReceived)}</td>,
                              dateCollected: <td key="dateCollected" style={seqW('dateCollected')}>{fmtLocalShort(r.dateCollected)}</td>,
                              client:        <td key="client" style={seqW('client')}>{r.client || ''}</td>,
                              projectId:     <td key="projectId" style={seqW('projectId')}>{r.projectId || ''}</td>,
                            };
                            return seqCm.visibleColumns.map(c => cell[c.key]);
                          })()}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Header right-click menu (Hide · Show All · Column Chooser) +
                      the Column Chooser popup — both portal to <body>, rendered once. */}
                  {seqMenu.headerMenu}
                  {seqCm.chooser}
                </div>
                <div className="qcount">Count={rows.length}</div>
              </div>
            </div>
          ) : (
            <div className="otherwrap">
              {/* Reagent / Instrument linking — legacy ReagentLink/InstrumentLink
                  parity, ported from the QC twin.  Pre-save picks stage locally and
                  ship with Create; a saved batch links/unlinks immediately.  The
                  Comment editor persists with the batch on OK. */}
              <OtherSection title="Reagent"
                onAdd={() => setModal('reagent')}
                onRemove={unlinkSelectedReagents}
                disabled={viewOnly}
                count={savedPrepId ? reagentRows.length : stagedReagents.length}
                head={<tr><th className="cbcol"><input type="checkbox" className="chk" checked={allReagentChecked} onChange={e => toggleAllReagentSel(e.target.checked)} /></th><th>ReagentID</th><th>ReagentName</th><th>ExpiredDate</th><th>DatePrepared</th><th>PreparedBy</th><th>COA</th><th>DateReceived</th><th>Vendor</th></tr>}
                body={savedPrepId ? (
                  reagentRows.length === 0
                    ? <tr><td colSpan={9} className="onodata">No reagents added.</td></tr>
                    : reagentRows.map(r => (
                      <tr key={r.oid} onClick={() => toggleReagentSel(r.oid)}>
                        <td className="cbcol"><input type="checkbox" className="chk" readOnly checked={reagentSel.has(r.oid)} onClick={e => e.stopPropagation()} onChange={() => toggleReagentSel(r.oid)} /></td>
                        <td>{r.reagentID}</td><td>{r.reagentName}</td>
                        <td>{fmtLocalDate(r.expiredDate)}</td><td>{fmtLocalShort(r.datePrepared)}</td>
                        <td>{r.preparedBy}</td>
                        <td>{r.hasCoa &&
                          <button className="coa-link" onClick={e => { e.stopPropagation(); void openCoa(r.reagentID); }}>COA</button>}</td>
                        <td>{fmtLocalShort(r.dateReceived)}</td><td>{r.vendorName}</td>
                      </tr>
                    ))
                ) : (
                  stagedReagents.length === 0
                    ? <tr><td colSpan={9} className="onodata">No reagents added.</td></tr>
                    : stagedReagents.map(r => (
                      <tr key={r.reagentID} onClick={() => toggleStagedReagentSel(r.reagentID ?? '')}>
                        <td className="cbcol"><input type="checkbox" className="chk" readOnly checked={stagedReagentSel.has(r.reagentID ?? '')} onClick={e => e.stopPropagation()} onChange={() => toggleStagedReagentSel(r.reagentID ?? '')} /></td>
                        <td>{r.reagentID}</td><td>{r.reagentName}</td>
                        <td>{fmtLocalDate(r.expiryDate)}</td><td>{fmtLocalShort(r.datePrepared)}</td>
                        <td>{r.preparedBy}</td>
                        <td />
                        <td>{fmtLocalShort(r.dateReceived)}</td><td>{r.vendor}</td>
                      </tr>
                    ))
                )} />
              <OtherSection title="Instrument"
                onAdd={() => setModal('instr2')}
                onRemove={unlinkSelectedInstruments}
                disabled={viewOnly}
                count={savedPrepId ? instrumentRows.length : stagedInstruments.length}
                head={<tr><th className="cbcol"><input type="checkbox" className="chk" checked={allInstrumentChecked} onChange={e => toggleAllInstrumentSel(e.target.checked)} /></th><th>InstrumentNumber</th><th>InstrumentName</th><th>Manufacturer</th><th>ExpirationDate</th><th>DepartmentUsed</th><th>Active</th><th>MaintenanceFrequency</th><th>Department</th><th>Category</th><th>RetiredDate</th></tr>}
                body={savedPrepId ? (
                  instrumentRows.length === 0
                    ? <tr><td colSpan={11} className="onodata">No instruments added.</td></tr>
                    : instrumentRows.map(r => (
                      <tr key={r.instrumentOid} onClick={() => toggleInstrumentSel(r.instrumentOid)}>
                        <td className="cbcol"><input type="checkbox" className="chk" readOnly checked={instrumentSel.has(r.instrumentOid)} onClick={e => e.stopPropagation()} onChange={() => toggleInstrumentSel(r.instrumentOid)} /></td>
                        <td>{r.labwareName}</td><td>{r.instrumentName}</td>
                        <td>{r.manufacturer}</td><td>{fmtLocalDate(r.expirationDate)}</td>
                        <td>{r.departmentUsedName}</td><td>{r.active === undefined ? '' : (r.active ? 'Yes' : 'No')}</td>
                        <td>{r.maintenanceFrequency}</td><td>{r.departmentNames}</td>
                        <td>{r.categoryName}</td><td>{fmtLocalDate(r.retiredDate)}</td>
                      </tr>
                    ))
                ) : (
                  stagedInstruments.length === 0
                    ? <tr><td colSpan={11} className="onodata">No instruments added.</td></tr>
                    : stagedInstruments.map(r => (
                      <tr key={r.instrumentOid} onClick={() => toggleStagedInstrumentSel(r.instrumentOid)}>
                        <td className="cbcol"><input type="checkbox" className="chk" readOnly checked={stagedInstrumentSel.has(r.instrumentOid)} onClick={e => e.stopPropagation()} onChange={() => toggleStagedInstrumentSel(r.instrumentOid)} /></td>
                        <td>{r.labwareName}</td><td>{r.instrumentName}</td>
                        <td>{r.manufacturer}</td><td>{fmtLocalDate(r.expirationDate)}</td>
                        <td>{r.departmentUsedName}</td><td>{r.active === undefined ? '' : (r.active ? 'Yes' : 'No')}</td>
                        <td>{r.maintenanceFrequency}</td><td>{r.departmentNames}</td>
                        <td>{r.categoryName}</td><td>{fmtLocalDate(r.retiredDate)}</td>
                      </tr>
                    ))
                )} />
              <div className="osect">
                <h3>Comment</h3>
                <div className="commentbox">
                  <textarea className="editor" value={comment} disabled={viewOnly}
                    placeholder={viewOnly ? '' : 'Add a comment for this batch…'}
                    onChange={e => setComment(e.target.value)} />
                </div>
              </div>
            </div>
          )}

          <div className="detail-actions">
            {!existingHeader && <button className="btn" disabled={!sorted} onClick={goPrevious}>‹ Previous</button>}
            {!sorted && <button className={`btn ${anySample ? 'primary' : ''}`} disabled={!anySample} onClick={buildSequence}>Sort ⇅</button>}
            {/* OK is the CREATE action — hidden once the batch has an Oid (a
                just-created or an opened existing batch). */}
            {sorted && !savedPrepId && !viewOnly && <button className="btn primary" onClick={onOk}>OK</button>}
            {/* OK is the EDIT-mode persist action (A1) — shown when an existing,
                still-editable (not ResultCompleted) batch is open in edit mode. */}
            {existingHeader && !viewOnly &&
              <button className="btn primary" onClick={saveEdits} disabled={!editDirty}
                title={editDirty ? undefined : 'No changes to save'}>OK</button>}
          </div>
        </div>

        {modal === 'addsample' && <AddSamplePicker
          candidates={addSampleCandidates}
          onClose={() => setModal(null)}
          onCommit={commitAddSample} />}
        {modal === 'notes' && jobOids[0] &&
          <NotesModal jobOid={jobOids[0]} jobLabel={jobLabel} onClose={() => { setModal(null); void loadNotesCount(); }} />}
        {/* Others-tab pickers open PRE-SEEDED with whatever's already linked/staged
            (RIGHT panel); OK returns the final set and reconcile* applies the delta. */}
        {modal === 'instr2' && <InstrPicker
          testMethodId={effTestMethodId || null}
          preLinked={savedPrepId ? instrumentRows : stagedInstruments}
          onClose={() => setModal(null)}
          onCommit={fin => { setModal(null); void reconcileInstruments(fin); }} />}
        {modal === 'reagent' && <ReagentPicker
          testMethodId={effTestMethodId || null}
          preLinked={
            savedPrepId
              ? reagentRows.map(r => ({ oid: r.oid, reagentID: r.reagentID, reagentName: r.reagentName, prepType: r.prepType, expiryDate: r.expiredDate, test: null, datePrepared: r.datePrepared, preparedBy: r.preparedBy, dateReceived: r.dateReceived }))
              : stagedReagents.map(r => ({ reagentID: r.reagentID, reagentName: r.reagentName, prepType: r.prepType, expiryDate: r.expiryDate, test: r.test, datePrepared: r.datePrepared, preparedBy: r.preparedBy, dateReceived: r.dateReceived }))
          }
          onClose={() => setModal(null)}
          onCommit={fin => { setModal(null); void reconcileReagents(fin); }} />}
        {modal === 'copy' && <CopyModal
          testMethodId={effTestMethodId}
          excludePrepOid={savedPrepId ?? undefined}
          onClose={() => setModal(null)}
          onApply={(src, reag, instr, comm) => { setModal(null); void copyFromPrevious(src, reag, instr, comm); }} />}
      </div>

      {/* right-click context menu — only rendered when it has an applicable item:
          Copy To All Cell (a copyable cell was focused) or Add Dilution (post-Sort).
          QC Batch parity — same behaviour as QcBatchDetailPage. */}
      {ctx && (focusedCol || sorted) && (
        <div className="qcb-ctxmenu" style={{ left: ctx.x, top: ctx.y }} onClick={e => e.stopPropagation()}>
          {focusedCol && <div onClick={() => copyToAll(ctx.row)}>▦ Copy To All Cell</div>}
          {sorted && <div onClick={() => void addDilution()}>💧 Add Dilution</div>}
        </div>
      )}
    </>
  );
}

// ── small field wrappers (copied from the QC twin) ───────────────────────────
function Fld({ label, req, yellow, children }: { label: string; req?: boolean; yellow?: boolean; children: ReactNode }) {
  return (
    <div className="fld">
      <label>{label}{req && <span className="star">*</span>}</label>
      <div className={`ctl ${yellow ? 'yellow' : ''}`}>{children}</div>
    </div>
  );
}
function PickerFld({ label, req, open, onToggle, text, placeholder, disabled, children }:
  { label: string; req?: boolean; open: boolean; onToggle: () => void; text: string; placeholder: boolean; disabled?: boolean; children: ReactNode }) {
  return (
    <div className="fld pos" onClick={e => e.stopPropagation()}>
      <label>{label}{req && <span className="star">*</span>}</label>
      <div className="ctl">
        <div className={`picker ${req && placeholder ? 'req' : ''} ${disabled ? 'disabled' : ''}`}
          onClick={disabled ? undefined : onToggle}>
          <span className={placeholder ? 'ph' : ''}>{text}</span><span className="car">▾</span>
        </div>
        {open && !disabled && children}
      </div>
    </div>
  );
}

// Short date for picker cells via the shared helper (§18.6 — never inline).
function fmtDate(v: string | null): string {
  return v ? fmtLocalDate(v) : '';
}

// Project Notes — job-scoped, reuses the EXISTING shared dbo.Notes stack (Custom
// Reporting's job-scoped endpoints; author gate + audit FormName "Project Notes"
// are shared, never reinvented).  Ported verbatim from the QC twin.
type ProjectNote = {
  oid: string; title: string | null; plainText: string | null; text: string | null;
  createdBy: string | null; createdDate: string | null; sort: number; canEdit: boolean;
};
function NotesModal({ jobOid, jobLabel, onClose }: { jobOid: string; jobLabel: string; onClose: () => void }) {
  const notify = useNotify();
  const base = `/api/v1/reporting/custom/${encodeURIComponent(jobOid)}/project-notes`;
  const [notes, setNotes]   = useState<ProjectNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]     = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editOid, setEditOid]   = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [text, setText]   = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(base); setNotes((r.data as { data?: ProjectNote[] }).data ?? []); }
    catch { notify.error('Could not load project notes.'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobOid]);
  useEffect(() => { void load(); }, [load]);

  const openAdd  = () => { setEditOid(null); setTitle(''); setText(''); setFormOpen(true); };
  const openEdit = (n: ProjectNote) => {
    if (!n.canEdit) { notify.error('You are not authorized to modify a note created by another author.'); return; }
    setEditOid(n.oid); setTitle(n.title ?? ''); setText(n.plainText ?? n.text ?? ''); setFormOpen(true);
  };
  const save = async () => {
    if (!title.trim() || !text.trim()) { notify.warning('Both Title and Note are required.'); return; }
    setBusy(true);
    try {
      if (editOid) {
        await api.put(`/api/v1/reporting/custom/notes/${encodeURIComponent(editOid)}`,
          { title: title.trim(), plainText: text.trim(), sort: 0, form: 'Project Notes' });
      } else {
        await api.post(base, { title: title.trim(), text: text.trim(), noteSource: 'Sample Prep Batch' });
      }
      setFormOpen(false);
      await load();
      prepMsg(editOid ? 'Note updated.' : 'Note added.', 'Project Notes');
    } catch (e) { notify.error(apiErrMsg(e, 'Saving the note failed.')); }
    finally { setBusy(false); }
  };
  const del = async (n: ProjectNote) => {
    if (!n.canEdit) { notify.error('You are not authorized to modify a note created by another author.'); return; }
    setBusy(true);
    try {
      await api.delete(`/api/v1/reporting/custom/project-notes/${encodeURIComponent(n.oid)}`);
      await load();
      prepMsg('Note deleted.', 'Project Notes');
    } catch (e) { notify.error(apiErrMsg(e, 'Deleting the note failed.')); }
    finally { setBusy(false); }
  };

  return (
    <Scrim onClose={onClose} width={880}>
      <div className="mh notes-mh">🗒 Project Notes — {jobLabel} <span className="x" onClick={onClose}>×</span></div>
      <div className="pb">
        <div style={{ marginBottom: 10 }}>
          <button className="btn primary" onClick={formOpen ? () => setFormOpen(false) : openAdd}>
            {formOpen ? 'Cancel' : '＋ Add Note'}
          </button>
        </div>
        {formOpen && (
          <div style={{ border: '1px solid var(--ai-line)', borderRadius: 6, padding: 12, marginBottom: 12, background: 'var(--ai-header-bg)' }}>
            <input style={{ width: '100%', marginBottom: 8, padding: '6px 9px', border: '1px solid var(--ai-line)', borderRadius: 6 }}
              placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} />
            <textarea style={{ width: '100%', minHeight: 80, padding: '6px 9px', border: '1px solid var(--ai-line)', borderRadius: 6 }}
              placeholder="Note" value={text} onChange={e => setText(e.target.value)} />
            <div style={{ marginTop: 8 }}><button className="btn primary" disabled={busy} onClick={save}>
              {editOid ? 'Update Note' : 'Save Note'}</button></div>
          </div>
        )}
        <div className="gridbox">
          <table><thead><tr><th>Title</th><th>Note</th><th>Author</th><th>Date</th><th /></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="onodata">Loading…</td></tr>
              ) : notes.length === 0 ? (
                <tr><td colSpan={5} className="onodata">No project notes for this job.</td></tr>
              ) : notes.map(n => (
                <tr key={n.oid}>
                  <td>{n.title}</td>
                  <td style={{ whiteSpace: 'normal' }}>{n.plainText ?? n.text}</td>
                  <td>{n.createdBy}</td>
                  <td>{fmtLocalShort(n.createdDate)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {n.canEdit && <>
                      <button className="btn" onClick={() => openEdit(n)}>Edit</button>{' '}
                      <button className="btn" disabled={busy} onClick={() => void del(n)}>Delete</button>
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mf"><button className="btn" onClick={onClose}>Close</button></div>
    </Scrim>
  );
}

function Scrim({ onClose, width, children }: { onClose: () => void; width: number; children: ReactNode }) {
  return (
    <div className="qcb-scrim" onClick={onClose}>
      <div className="pickmodal" style={{ width }} onClick={e => e.stopPropagation()}>{children}</div>
    </div>
  );
}

// Copy From Previous Batch — prep twin of QcBatchDetailPage.CopyModal. OG
// SamplePreparationController.btnCopyFromSamplePrepAction_Execute (:6125) +
// CopyingFromPreviousBatch (:6173): pick a prior batch (same test method), tick
// which of its parts to copy — Reagent · Instrument · Others(=Comment) · All the
// Above — OK → append + de-dup into this batch (handled by copyFromPrevious in the
// page). "All the Above" ticks/copies all three.
function CopyModal({ testMethodId, excludePrepOid, onClose, onApply }: {
  testMethodId: string; excludePrepOid?: string;
  onClose: () => void; onApply: (sourcePrepOid: string, copyReagents: boolean, copyInstruments: boolean, copyOthers: boolean) => void;
}) {
  const notify = useNotify();
  const [list, setList]       = useState<PrepPreviousBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel]         = useState<string | null>(null);
  // All checkboxes start UNCHECKED on open (matches OG, where the
  // CopyFromSamplePrepBatch BO booleans default false). cpOthers = copy the Comment.
  const [cpReagent, setCpReagent] = useState(false);
  const [cpInstr, setCpInstr]     = useState(false);
  const [cpOthers, setCpOthers]   = useState(false);
  // "All the Above" — master checkbox: checked iff all three are, toggling it
  // sets all three (OG AllAbove → copyReagent = copyInstrument = copyOthers).
  const allAbove = cpReagent && cpInstr && cpOthers;
  const setAllAbove = (v: boolean) => { setCpReagent(v); setCpInstr(v); setCpOthers(v); };
  useEffect(() => {
    prepBatchApi.previousBatches(testMethodId, excludePrepOid)
      .then(setList)
      .catch(() => notify.error('Could not load previous batches.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testMethodId, excludePrepOid]);
  const ok = () => {
    if (!sel) { notify.warning('Select a prep batch to copy from.'); return; }               // legacy guard
    if (!cpReagent && !cpInstr && !cpOthers) { notify.warning('Select at least one item to copy.'); return; }  // legacy "selectchkbox"
    onApply(sel, cpReagent, cpInstr, cpOthers);
  };
  return (
    <Scrim onClose={onClose} width={760}>
      <div className="mh">📋 Copy From Previous Batch <span className="x" onClick={onClose}>×</span></div>
      <div className="pb">
        <div style={{ display: 'flex', gap: 20, marginBottom: 10, fontSize: 13 }}>
          <label><input type="checkbox" checked={cpReagent} onChange={e => setCpReagent(e.target.checked)} /> Reagent</label>
          <label><input type="checkbox" checked={cpInstr} onChange={e => setCpInstr(e.target.checked)} /> Instrument</label>
          <label><input type="checkbox" checked={cpOthers} onChange={e => setCpOthers(e.target.checked)} /> Others</label>
          <label><input type="checkbox" checked={allAbove} onChange={e => setAllAbove(e.target.checked)} /> All the Above</label>
        </div>
        <div className="gridbox">
          <table><thead><tr><th className="cbcol" /><th>PrepBatchID</th><th>Matrix</th><th>Status</th><th>Date</th><th className="num">Reagents</th><th className="num">Instruments</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="onodata">Loading…</td></tr>
              ) : list.length === 0 ? (
                <tr><td colSpan={7} className="onodata">No previous batches for this test.</td></tr>
              ) : list.map(b => (
                <tr key={b.prepOid} className={sel === b.prepOid ? 'sel' : ''} style={{ cursor: 'pointer' }} onClick={() => setSel(b.prepOid)}>
                  <td className="cbcol"><input type="radio" readOnly checked={sel === b.prepOid} /></td>
                  <td>{b.prepBatchId}</td><td>{b.matrix}</td><td>{b.statusText}</td><td>{fmtLocalShort(b.createdDate)}</td>
                  <td className="num">{b.reagentCount}</td><td className="num">{b.instrumentCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mf"><button className="btn primary" onClick={ok}>OK</button><button className="btn" onClick={onClose}>Cancel</button></div>
    </Scrim>
  );
}

// Add Sample picker — the AI equivalent of OG's SampleLogIn_ListView_Preparation
// popup (SamplePreparationController.AddSample_Execute). Lists the pending-prep
// candidate samples (already excluding the ones in the sequence, done server-side)
// with checkboxes; OK returns the ticked candidates to commitAddSample.
function AddSamplePicker({ candidates, onClose, onCommit }:
  { candidates: PrepSample[]; onClose: () => void; onCommit: (selected: PrepSample[]) => void }) {
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const toggle = (oid: string) =>
    setSel(prev => { const n = new Set(prev); if (n.has(oid)) n.delete(oid); else n.add(oid); return n; });
  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => q
    ? candidates.filter(c => `${c.sysSampleCode ?? ''} ${c.jobID ?? ''} ${c.sampleName ?? ''}`.toLowerCase().includes(q))
    : candidates, [candidates, q]);
  const allChecked = filtered.length > 0 && filtered.every(c => sel.has(c.sampleLogInOid));
  const toggleAll = (on: boolean) =>
    setSel(prev => {
      const n = new Set(prev);
      for (const c of filtered) { if (on) n.add(c.sampleLogInOid); else n.delete(c.sampleLogInOid); }
      return n;
    });
  const ok = () => onCommit(candidates.filter(c => sel.has(c.sampleLogInOid)));
  return (
    <Scrim onClose={onClose} width={760}>
      <div className="mh">➕ Add Sample <span className="x" onClick={onClose}>×</span></div>
      <div className="pb">
        <div className="list-search" style={{ marginBottom: 8 }}>
          <i className="ti ti-search" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" />
          {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
        </div>
        <div className="gridbox">
          <table>
            <thead><tr>
              <th className="cbcol"><input type="checkbox" className="chk" checked={allChecked}
                onChange={e => toggleAll(e.target.checked)} /></th>
              <th>SysSampleCode</th><th>JobID</th><th>SampleName</th>
            </tr></thead>
            <tbody>
              {filtered.length === 0
                ? <tr><td colSpan={4} className="onodata">No samples available to add.</td></tr>
                : filtered.map(c => (
                  <tr key={c.sampleLogInOid} onClick={() => toggle(c.sampleLogInOid)}>
                    <td className="cbcol"><input type="checkbox" className="chk" readOnly
                      checked={sel.has(c.sampleLogInOid)}
                      onClick={e => e.stopPropagation()}
                      onChange={() => toggle(c.sampleLogInOid)} /></td>
                    <td>{c.sysSampleCode}</td><td>{c.jobID}</td><td>{c.sampleName}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="foot"><span>{filtered.length} items</span></div>
      </div>
      <div className="mf">
        <button className="btn primary" disabled={sel.size === 0} onClick={ok}>OK</button>
        <button className="btn" onClick={onClose}>Cancel</button>
      </div>
    </Scrim>
  );
}

// ── Others-tab shared section (Add/Remove toolbar + grid + footer) ────────────
// Ported verbatim from the QC twin.
function OtherSection({ title, onAdd, onRemove, count, head, body, disabled }:
  { title: string; onAdd: () => void; onRemove: () => void; count: number; head: ReactNode; body: ReactNode; disabled?: boolean }) {
  return (
    <div className="osect">
      <h3>{title}</h3>
      <div className="subtb">
        <button onClick={onAdd} disabled={disabled}><span className="scirc g">＋</span> Add</button>
        <button onClick={onRemove} disabled={disabled}><span className="scirc r">−</span> Remove</button>
      </div>
      <div className="gridbox"><table><thead>{head}</thead><tbody>{body}</tbody></table></div>
      <div className="foot"><span>Page 1 of 1 ({count} items)</span>
        <div className="pages"><span className="pg">‹</span><b>1</b><span className="pg">›</span></div>
        <div className="psize">Page size: <select defaultValue="20"><option>20</option></select></div></div>
    </div>
  );
}

// Others-tab instrument picker — TRUE dual-panel design, ported from the QC twin
// (InstrPicker). RIGHT panel opens PRE-SEEDED with the batch's current instruments;
// OK returns the final set; the caller computes the add/remove delta.
function InstrPicker({ preLinked, testMethodId, onClose, onCommit }:
  { preLinked: QcInstrument[]; testMethodId?: string | null;
    onClose: () => void; onCommit: (final: QcInstrument[]) => void }) {
  const notify = useNotify();
  const [options, setOptions] = useState<QcInstrument[]>([]);
  const [loading, setLoading] = useState(true);
  const [staged, setStaged] = useState<QcInstrument[]>(preLinked);
  const [searchL, setSearchL] = useState('');
  const [searchR, setSearchR] = useState('');
  useEffect(() => {
    prepBatchApi.availableInstruments(testMethodId)
      .then(all => {
        const known = new Set(all.map(i => i.instrumentOid));
        const extra = preLinked.filter(i => !known.has(i.instrumentOid));
        setOptions([...all, ...extra]);
      })
      .catch(() => notify.error('Could not load the instrument catalog.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const stagedIds = useMemo(() => new Set(staged.map(s => s.instrumentOid)), [staged]);
  const match = (i: QcInstrument, q: string) =>
    `${i.labwareID ?? ''} ${i.instrumentName ?? ''} ${i.labwareName ?? ''}`.toLowerCase().includes(q);
  const available = useMemo(() => {
    const q = searchL.trim().toLowerCase();
    return options.filter(i => !stagedIds.has(i.instrumentOid) && (!q || match(i, q)));
  }, [options, stagedIds, searchL]);
  const selected = useMemo(() => {
    const q = searchR.trim().toLowerCase();
    return q ? staged.filter(i => match(i, q)) : staged;
  }, [staged, searchR]);
  const addOne = (i: QcInstrument) => setStaged(prev => [...prev, i]);
  const removeOne = (oid: string) => setStaged(prev => prev.filter(s => s.instrumentOid !== oid));
  const Cols = () => (
    <colgroup>
      <col style={{ width: 34 }} />
      <col style={{ width: '22%' }} />
      <col style={{ width: '44%' }} />
      <col style={{ width: '34%' }} />
    </colgroup>
  );
  return (
    <Scrim onClose={onClose} width={980}>
      <div className="mh">🔬 Instruments <span className="x" onClick={onClose}>×</span></div>
      <div className="pb"><div className="dual">
        <div className="dualcol">
          <div className="ds"><input value={searchL} onChange={e => setSearchL(e.target.value)} placeholder="Enter text to search…" /></div>
          <div className="dscroll"><table><Cols />
            <thead><tr><th className="cbcol" /><th>InstrumentID</th><th>InstrumentName</th><th>InstrumentNumber</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="onodata">Loading…</td></tr>
              ) : available.length === 0 ? (
                <tr><td colSpan={4} className="onodata">No matches</td></tr>
              ) : available.map(i => (
                <tr key={i.instrumentOid}>
                  <td className="cbcol"><button className="circ add" onClick={() => addOne(i)}>＋</button></td>
                  <td>{i.labwareID || ''}</td><td>{i.instrumentName || ''}</td><td>{i.labwareName || ''}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
          <div className="dfoot"><span>{available.length} items</span><span>Page size: 100</span></div>
        </div>
        <div className="dualcol">
          <div className="ds"><input value={searchR} onChange={e => setSearchR(e.target.value)} placeholder="Enter text to search…" /></div>
          <div className="dscroll"><table><Cols />
            <thead><tr><th className="cbcol" /><th>InstrumentID</th><th>InstrumentName</th><th>InstrumentNumber</th></tr></thead>
            <tbody>
              {selected.length === 0 ? (
                <tr><td colSpan={4} className="onodata">Nothing selected</td></tr>
              ) : selected.map(i => (
                <tr key={i.instrumentOid}>
                  <td className="cbcol"><button className="circ rm" onClick={() => removeOne(i.instrumentOid)}>−</button></td>
                  <td>{i.labwareID || ''}</td><td>{i.instrumentName || ''}</td><td>{i.labwareName || ''}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
          <div className="dfoot"><span>{selected.length} items</span><span>Page size: 100</span></div>
        </div>
      </div></div>
      <div className="mf">
        <button className="btn primary" onClick={() => onCommit(staged)}>OK</button>
        <button className="btn" onClick={onClose}>Cancel</button>
      </div>
    </Scrim>
  );
}

// Reagent staged rows are a UNION: pre-linked rows (carry an Oid → remove becomes
// unlinkReagent) OR newly-picked catalog rows (no Oid yet → add becomes
// linkReagent). ReagentID is the merge key. Ported from the QC twin.
type StagedReagent = {
  reagentID:   string;
  reagentName: string | null;
  prepType:    string | null;
  expiryDate:  string | null;
  test:        string | null;
  datePrepared: string | null;
  preparedBy:  string | null;
  dateReceived: string | null;
  linkedOid?:  string;
  catalog?:    QcAvailableReagent;
};
type PreLinkedReagent = {
  oid?: string;
  reagentID:   string | null;
  reagentName: string | null;
  prepType:    string | null;
  expiryDate:  string | null;
  test?:       string | null;
  datePrepared?: string | null;
  preparedBy?: string | null;
  dateReceived?: string | null;
};
// Date-filter above the Available panel (default "1Y"); "All" removes the filter.
const REAGENT_DATE_FILTERS: { id: string; months?: number; years?: number }[] = [
  { id: '1M', months: 1 }, { id: '3M', months: 3 }, { id: '6M', months: 6 },
  { id: '1Y', years: 1 }, { id: '2Y', years: 2 }, { id: '5Y', years: 5 },
  { id: 'All' },
];
function ReagentPicker({ preLinked, testMethodId, onClose, onCommit }:
  { preLinked: PreLinkedReagent[]; testMethodId?: string | null;
    onClose: () => void; onCommit: (final: StagedReagent[]) => void }) {
  const notify = useNotify();
  const [options, setOptions] = useState<QcAvailableReagent[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('1Y');
  const [staged, setStaged] = useState<StagedReagent[]>(
    preLinked
      .filter(p => !!p.reagentID)
      .map(p => ({
        reagentID: p.reagentID!,
        reagentName: p.reagentName,
        prepType: p.prepType,
        expiryDate: p.expiryDate,
        test: p.test ?? null,
        datePrepared: p.datePrepared ?? null,
        preparedBy: p.preparedBy ?? null,
        dateReceived: p.dateReceived ?? null,
        linkedOid: p.oid,
      })));
  const [searchL, setSearchL] = useState('');
  const [searchR, setSearchR] = useState('');
  useEffect(() => {
    // Scoped to the batch's test (OG SamplePreparationController.cs:1292 passes the
    // TestMethod Oid); unscoped, this listed every reagent in the lab.
    prepBatchApi.availableReagents(testMethodId)
      .then(setOptions)
      .catch(() => notify.error('Could not load the reagent catalog.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const stagedIds = useMemo(() => new Set(staged.map(s => s.reagentID)), [staged]);
  const match = (id: string, name: string | null, prep: string | null, q: string) =>
    `${id} ${name ?? ''} ${prep ?? ''}`.toLowerCase().includes(q);
  const dateFilterCutoff = useMemo(() => {
    const opt = REAGENT_DATE_FILTERS.find(o => o.id === dateFilter);
    if (!opt || (!opt.months && !opt.years)) return null;
    const d = new Date();
    if (opt.months) d.setMonth(d.getMonth() - opt.months);
    if (opt.years) d.setFullYear(d.getFullYear() - opt.years);
    return d;
  }, [dateFilter]);
  const available = useMemo(() => {
    const q = searchL.trim().toLowerCase();
    return options.filter(o => {
      if (!o.reagentID || stagedIds.has(o.reagentID)) return false;
      if (dateFilterCutoff) {
        const raw = o.dateReceived ?? o.datePrepared;
        if (!raw) return false;
        const d = new Date(raw);
        if (Number.isNaN(d.getTime()) || d < dateFilterCutoff) return false;
      }
      return !q || match(o.reagentID, o.reagentName, o.prepType, q);
    });
  }, [options, stagedIds, searchL, dateFilterCutoff]);
  const selected = useMemo(() => {
    const q = searchR.trim().toLowerCase();
    return q ? staged.filter(s => match(s.reagentID, s.reagentName, s.prepType, q)) : staged;
  }, [staged, searchR]);
  const addOne = (o: QcAvailableReagent) => {
    if (!o.reagentID) return;
    setStaged(prev => [...prev, {
      reagentID: o.reagentID!,
      reagentName: o.reagentName,
      prepType: o.prepType,
      expiryDate: o.expiryDate,
      test: o.test,
      datePrepared: o.datePrepared,
      preparedBy: o.preparedBy,
      dateReceived: o.dateReceived,
      catalog: o,
    }]);
  };
  const removeOne = (reagentID: string) => setStaged(prev => prev.filter(s => s.reagentID !== reagentID));
  const REAGENT_COL_WIDTHS = [44, 110, 150, 100, 110, 120, 110, 100, 110];
  const reagentTableWidth = REAGENT_COL_WIDTHS.reduce((a, b) => a + b, 0);
  const Cols = () => (
    <colgroup>
      {REAGENT_COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
    </colgroup>
  );
  return (
    <Scrim onClose={onClose} width={1180}>
      <div className="mh">📦 Add Reagents <span className="x" onClick={onClose}>×</span></div>
      <div className="pb">
        <div className="reag-datefilter">
          <label htmlFor="reag-datefilter-sel">Date Filter</label>
          <select id="reag-datefilter-sel" value={dateFilter} onChange={e => setDateFilter(e.target.value)}>
            {REAGENT_DATE_FILTERS.map(o => <option key={o.id} value={o.id}>{o.id}</option>)}
          </select>
        </div>
        <div className="dual">
        <div className="dualcol">
          <div className="ds"><input value={searchL} onChange={e => setSearchL(e.target.value)} placeholder="Enter text to search…" /></div>
          <div className="dscroll wide"><table className="wide" style={{ width: reagentTableWidth }}><Cols />
            <thead><tr><th className="cbcol" /><th>ReagentID</th><th>ReagentName</th><th>PrepType</th><th>ExpirationDate</th><th>Test</th><th>DatePrepared</th><th>PreparedBy</th><th>DateReceived</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="onodata">Loading…</td></tr>
              ) : available.length === 0 ? (
                <tr><td colSpan={9} className="onodata">No matches</td></tr>
              ) : available.map(o => (
                <tr key={o.reagentID}>
                  <td className="cbcol"><button className="circ add" onClick={() => addOne(o)}>＋</button></td>
                  <td>{o.reagentID}</td><td>{o.reagentName}</td><td>{o.prepType || ''}</td><td>{fmtDate(o.expiryDate)}</td>
                  <td>{o.test || ''}</td><td>{fmtDate(o.datePrepared)}</td><td>{o.preparedBy || ''}</td><td>{fmtDate(o.dateReceived)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
          <div className="dfoot"><span>{available.length} items</span><span>Page size: 20</span></div>
        </div>
        <div className="dualcol">
          <div className="ds"><input value={searchR} onChange={e => setSearchR(e.target.value)} placeholder="Enter text to search…" /></div>
          <div className="dscroll wide"><table className="wide" style={{ width: reagentTableWidth }}><Cols />
            <thead><tr><th className="cbcol" /><th>ReagentID</th><th>ReagentName</th><th>PrepType</th><th>ExpirationDate</th><th>Test</th><th>DatePrepared</th><th>PreparedBy</th><th>DateReceived</th></tr></thead>
            <tbody>
              {selected.length === 0 ? (
                <tr><td colSpan={9} className="onodata">Nothing selected</td></tr>
              ) : selected.map(s => (
                <tr key={s.reagentID}>
                  <td className="cbcol"><button className="circ rm" onClick={() => removeOne(s.reagentID)}>−</button></td>
                  <td>{s.reagentID}</td><td>{s.reagentName}</td><td>{s.prepType || ''}</td><td>{fmtDate(s.expiryDate)}</td>
                  <td>{s.test || ''}</td><td>{fmtDate(s.datePrepared)}</td><td>{s.preparedBy || ''}</td><td>{fmtDate(s.dateReceived)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
          <div className="dfoot"><span>{selected.length} items</span><span>Page size: 20</span></div>
        </div>
      </div></div>
      <div className="mf">
        <button className="btn primary" onClick={() => onCommit(staged)}>OK</button>
        <button className="btn" onClick={onClose}>Cancel</button>
      </div>
    </Scrim>
  );
}
